import React, { useState, useRef, useEffect, useMemo } from "react";
import html2pdf from "html2pdf.js";
import axios from "axios";
import { format } from "date-fns";
import { FileText, Plane, User, AlertTriangle, MessageSquare, Download, Loader2, CloudRain, Sun, Cloud, CloudFog, CloudOff, Save, X, ChevronDown, Check, FileCode, ChevronsUpDown, Send, Skull, Lock, HelpCircle, Sparkles, Copy, CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FlightRouteWeather from "@/components/FlightRouteWeather";
import AIWeatherPrediction from "@/components/AIWeatherPrediction";
import { cn } from "@/lib/utils";

// Default PIC crew members list (fallback if admin config not loaded)
const DEFAULT_PIC_CREW_MEMBERS = [
  { value: "FDT", label: "FDT" },
  { value: "MVE", label: "MVE" },
  { value: "NMA", label: "NMA" },
  { value: "PVG", label: "PVG" },
  { value: "SDW", label: "SDW" },
  { value: "SKI", label: "SKI" },
  { value: "SME", label: "SME" },
  { value: "TGE", label: "TGE" },
];

const DEFAULT_LIAISON_OFFICERS = [
  { value: "BRS", label: "BRS" },
  { value: "BTY", label: "BTY" },
  { value: "DLN", label: "DLN" },
  { value: "DOT", label: "DOT" },
  { value: "MCM", label: "MCM" },
  { value: "SRB", label: "SRB" },
  { value: "XDU", label: "XDU 🥳" },
];

const DEFAULT_AIRCRAFT = [
  { value: "da42mpp", label: "Diamond DA42 MPP" },
  { value: "da62mpp", label: "Diamond DA62 MPP" },
];

const DEFAULT_FTX_DESIGNATORS = ["SPARROW 3"];

// Airport coordinates database
const AIRPORT_DATA = {
  // Netherlands
  EHAM: { lat: 52.31, lon: 4.76, name: "Amsterdam Schiphol" },
  EHRD: { lat: 51.96, lon: 4.44, name: "Rotterdam The Hague" },
  EHEH: { lat: 51.45, lon: 5.37, name: "Eindhoven" },
  EHGG: { lat: 53.12, lon: 6.58, name: "Groningen Eelde" },
  // Belgium
  EBBR: { lat: 50.90, lon: 4.48, name: "Brussels" },
  EBOS: { lat: 51.20, lon: 2.86, name: "Ostend-Bruges" },
  EBAW: { lat: 51.19, lon: 4.46, name: "Antwerp" },
  EBCI: { lat: 50.46, lon: 4.45, name: "Charleroi Brussels South" },
  EBLG: { lat: 50.64, lon: 5.44, name: "Liège" },
  // UK
  EGLL: { lat: 51.47, lon: -0.46, name: "London Heathrow" },
  EGKK: { lat: 51.15, lon: -0.18, name: "London Gatwick" },
  EGCC: { lat: 53.35, lon: -2.28, name: "Manchester" },
  EGGW: { lat: 51.87, lon: -0.37, name: "London Luton" },
  EGSS: { lat: 51.89, lon: 0.24, name: "London Stansted" },
  // Germany
  EDDF: { lat: 50.03, lon: 8.57, name: "Frankfurt" },
  EDDM: { lat: 48.35, lon: 11.79, name: "Munich" },
  EDDB: { lat: 52.36, lon: 13.51, name: "Berlin Brandenburg" },
  EDDL: { lat: 51.28, lon: 6.77, name: "Düsseldorf" },
  EDDH: { lat: 53.63, lon: 9.99, name: "Hamburg" },
  // France
  LFPG: { lat: 49.01, lon: 2.55, name: "Paris Charles de Gaulle" },
  LFPO: { lat: 48.72, lon: 2.36, name: "Paris Orly" },
  LFLL: { lat: 45.72, lon: 5.08, name: "Lyon" },
  LFMN: { lat: 43.66, lon: 7.22, name: "Nice Côte d'Azur" },
  // USA
  KJFK: { lat: 40.64, lon: -73.78, name: "New York JFK" },
  KLAX: { lat: 33.94, lon: -118.41, name: "Los Angeles" },
  KORD: { lat: 41.98, lon: -87.90, name: "Chicago O'Hare" },
  KATL: { lat: 33.64, lon: -84.43, name: "Atlanta" },
  // Default - North Sea
  DEFAULT: { lat: 52.3, lon: 4.0, name: "" }
};

const getAirportCoords = (icao) => {
  const data = AIRPORT_DATA[icao?.toUpperCase()] || AIRPORT_DATA.DEFAULT;
  return { lat: data.lat, lon: data.lon };
};

const getAirportName = (icao) => {
  const data = AIRPORT_DATA[icao?.toUpperCase()];
  return data?.name || "";
};
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const BriefingForm = ({ 
  metar, 
  taf, 
  explanation, 
  icao, 
  flightCategory, 
  isMultiAirport, 
  multiAirportData,
  editingBriefing,
  onSaveSuccess,
  onClearEdit
}) => {
  const [formData, setFormData] = useState({
    ftxDesignator: "",
    registration: "",
    aircraftType: "",
    callsign: "",
    missionWeather: "",
    crewRecommendations: "",
    crewPIC: "",
    crewPM: "",
    crewLiaisonOfficer: "",
    crewSensorOperator: "",
    missionWindowTiming: "",
    missionDate: new Date().toISOString().split('T')[0], // Default to today
    // Flight limitations
    poorVisibility: false,
    lowClouds: false,
    crosswind: false,
    thunderstorm: false,
    icingConditions: false,
  });
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [briefingTitle, setBriefingTitle] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [picComboOpen, setPicComboOpen] = useState(false);
  const [notStandardRouting, setNotStandardRouting] = useState(false);
  const [routeMapImage, setRouteMapImage] = useState(null);
  const [showAIPrediction, setShowAIPrediction] = useState(true);
  const [weatherPredictionData, setWeatherPredictionData] = useState(null);
  const [windyMapsData, setWindyMapsData] = useState({ visible: false, gifs: {} });
  const [windySnapshots, setWindySnapshots] = useState([]); // Changed to array for multiple snapshots
  const [logoBase64, setLogoBase64] = useState(null);
  const [radarImage, setRadarImage] = useState(null);
  const [radarImageBase64, setRadarImageBase64] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [includeNotams, setIncludeNotams] = useState(false);
  const [notams, setNotams] = useState({}); // Changed to object: { EBOS: [...], EBKT: [...] }
  const [notamsLoading, setNotamsLoading] = useState(false);
  const [notamExplanations, setNotamExplanations] = useState({}); // AI explanations
  const [selectedSortie, setSelectedSortie] = useState([0]); // Array of selected sortie indices (supports multi-select)
  const [picSearchValue, setPicSearchValue] = useState(""); // Separate search value for PIC dropdown
  const [notamExplanationsLoading, setNotamExplanationsLoading] = useState(false);
  const [showRawNotams, setShowRawNotams] = useState(false); // Toggle raw/explained view
  const [dismissedNotams, setDismissedNotams] = useState(new Set()); // Track dismissed NOTAM IDs
  const [hiddenWeatherAirports, setHiddenWeatherAirports] = useState([]);
  const [hiddenSingleAirport, setHiddenSingleAirport] = useState(false);
  // State for saved/snapshot data (when loading from library)
  const [savedWeatherData, setSavedWeatherData] = useState(null);
  const [isLoadedFromLibrary, setIsLoadedFromLibrary] = useState(false);
  // Creation timestamp - set when form is first used or loaded
  const [briefingCreatedAt, setBriefingCreatedAt] = useState(new Date().toISOString());
  // Superuser mode for editing locked briefings
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [showSuperuserDialog, setShowSuperuserDialog] = useState(false);
  const [superuserPassword, setSuperuserPassword] = useState("");
  // AI Help dialog
  const [showAiHelpDialog, setShowAiHelpDialog] = useState(false);
  // Copy to clipboard state
  const [copiedAirport, setCopiedAirport] = useState(null);
  // Admin config state for dynamic dropdowns
  const [adminConfig, setAdminConfig] = useState(null);
  // SkyLegs autofill state
  const [skylegsLoading, setSkylegsLoading] = useState(false);
  const [skylegsData, setSkylegsData] = useState(null);
  const formRef = useRef(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Fetch flight data from SkyLegs when FTX designator changes or date changes
  const fetchSkylegsFlightData = async (ftxDesignator, missionDate = null) => {
    if (!ftxDesignator || ftxDesignator === 'NONE') {
      setSkylegsData(null);
      return;
    }
    
    const dateParam = missionDate || formData.missionDate || new Date().toISOString().split('T')[0];
    
    setSkylegsLoading(true);
    try {
      const response = await axios.get(`${API}/ops/skylegs/flight-by-ftx/${encodeURIComponent(ftxDesignator)}?date=${dateParam}`);
      const data = response.data;
      
      if (data.found) {
        setSkylegsData(data);
        setSelectedSortie([0]); // Reset to first sortie (as array for multi-select)
        
        // Get timing from first sortie (default)
        const sortieData = data.sorties?.[0] || data;
        const missionWindow = (sortieData.departure_time && sortieData.arrival_time) 
          ? `${sortieData.departure_time}Z - ${sortieData.arrival_time}Z` 
          : '';
        
        // Autofill form fields
        setFormData(prev => {
          // Auto-select aircraft type based on registration (same logic as handleRegistrationChange)
          // OO-MSC → DA42 MPP, all others → DA62 MPP
          let autoAircraftType = prev.aircraftType;
          if (data.registration) {
            if (data.registration === 'OO-MSC') {
              autoAircraftType = 'diamondda42mpp';
            } else {
              autoAircraftType = 'diamondda62mpp';
            }
          }
          
          return {
            ...prev,
            registration: data.registration || prev.registration,
            aircraftType: autoAircraftType,
            callsign: data.callsign || prev.callsign,
            missionWindowTiming: missionWindow,
            crewPIC: data.crew?.pic || prev.crewPIC,
            crewSensorOperator: data.crew?.sensor_operator || prev.crewSensorOperator,
            crewLiaisonOfficer: data.crew?.liaison_officer || prev.crewLiaisonOfficer,
            missionDate: data.requested_date || dateParam, // Update to actual flight date
          };
        });
        
        // Check if all flights for this date are complete - auto-advance to next day
        if (data.all_flights_complete && dateParam === new Date().toISOString().split('T')[0]) {
          // All flights for today are complete, suggest moving to tomorrow
          const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
          toast.info(`All ${data.total_sorties} sortie(s) for today are complete. Loading tomorrow's schedule...`, {
            duration: 3000,
          });
          
          // Auto-fetch tomorrow's flights after a brief delay
          setTimeout(() => {
            fetchSkylegsFlightData(ftxDesignator, tomorrow);
          }, 1500);
          return;
        }
        
        const sortieInfo = data.total_sorties > 1 
          ? ` (${data.total_sorties} sorties scheduled)` 
          : '';
        toast.success(`Flight data loaded from SkyLegs: ${data.crew?.all?.join(', ') || 'No crew assigned'}${sortieInfo}`, {
          duration: 4000,
        });
      } else {
        setSkylegsData(null);
        // Only log, don't show toast for non-critical issues
        if (data.api_unavailable || data.rate_limited) {
          console.log('SkyLegs API unavailable or rate-limited:', data.message);
        } else {
          // Don't show error toast for "no flight today" - it's expected
          console.log(`SkyLegs: ${data.message}`);
        }
      }
    } catch (error) {
      console.error('Error fetching SkyLegs data:', error);
      setSkylegsData(null);
      // Only log connection errors, don't spam user with toasts
      console.log('SkyLegs API unreachable - flight data autofill unavailable');
    } finally {
      setSkylegsLoading(false);
    }
  };

  // Fetch admin config on mount
  useEffect(() => {
    const fetchAdminConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/config`);
        setAdminConfig(response.data);
      } catch (error) {
        console.error("Failed to fetch admin config:", error);
        // Will use default values if fetch fails
      }
    };
    fetchAdminConfig();
  }, [API]);

  // Handle sortie selection change (supports multi-select)
  const handleSortieChange = (sortieIndex) => {
    setSelectedSortie(prev => {
      let newSelection;
      if (prev.includes(sortieIndex)) {
        // Deselect - but ensure at least one sortie remains selected
        newSelection = prev.filter(idx => idx !== sortieIndex);
        if (newSelection.length === 0) {
          newSelection = [sortieIndex]; // Keep at least one selected
        }
      } else {
        // Select - add to selection
        newSelection = [...prev, sortieIndex].sort((a, b) => a - b);
      }
      
      // Update mission window and crew based on selection
      if (skylegsData?.sorties && newSelection.length > 0) {
        const sortedIndices = [...newSelection].sort((a, b) => a - b);
        const firstSortie = skylegsData.sorties[sortedIndices[0]];
        const lastSortie = skylegsData.sorties[sortedIndices[sortedIndices.length - 1]];
        
        if (firstSortie && lastSortie) {
          setFormData(prevForm => ({
            ...prevForm,
            missionWindowTiming: `${firstSortie.departure_time}Z - ${lastSortie.arrival_time}Z`,
            // Update crew to match the first selected sortie
            crewPIC: firstSortie.crew?.pic || prevForm.crewPIC,
            crewSensorOperator: firstSortie.crew?.sensor_operator || prevForm.crewSensorOperator,
            crewLiaisonOfficer: firstSortie.crew?.liaison_officer || prevForm.crewLiaisonOfficer,
          }));
        }
      }
      
      return newSelection;
    });
  };

  // Dynamic dropdown values from admin config or defaults
  const PIC_CREW_MEMBERS = useMemo(() => {
    if (adminConfig?.pilots?.length > 0) {
      return adminConfig.pilots.map(p => ({ value: p, label: p }));
    }
    return DEFAULT_PIC_CREW_MEMBERS;
  }, [adminConfig]);

  const LIAISON_OFFICERS = useMemo(() => {
    if (adminConfig?.liaisonOfficers?.length > 0) {
      return adminConfig.liaisonOfficers.map(lo => ({ 
        value: lo, 
        label: lo.includes('XDU') ? (lo.includes('🥳') ? lo : lo + ' 🥳') : lo 
      }));
    }
    return DEFAULT_LIAISON_OFFICERS;
  }, [adminConfig]);

  const AIRCRAFT_TYPES = useMemo(() => {
    if (adminConfig?.aircraft?.length > 0) {
      return adminConfig.aircraft.map(ac => ({
        value: ac.toLowerCase().replace(/\s+/g, ''),
        label: ac
      }));
    }
    return DEFAULT_AIRCRAFT;
  }, [adminConfig]);

  const FTX_DESIGNATORS = useMemo(() => {
    if (adminConfig?.ftxDesignators?.length > 0) {
      return adminConfig.ftxDesignators;
    }
    return DEFAULT_FTX_DESIGNATORS;
  }, [adminConfig]);

  // Copy airport weather to clipboard
  const handleCopyWeather = async (airport, icaoCode, name) => {
    let text = `${icaoCode || airport?.icao} - ${name || airport?.name || getAirportName(icaoCode || airport?.icao)}\n`;
    const metarRaw = airport?.metar?.raw || airport?.raw;
    const tafRaw = airport?.taf?.raw;
    
    if (metarRaw) {
      text += `\nMETAR:\n${metarRaw}`;
    }
    if (tafRaw) {
      text += `\n\nTAF:\n${tafRaw}`;
    }
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAirport(icaoCode || airport?.icao);
      setTimeout(() => setCopiedAirport(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Check if briefing is locked (more than 4 hours old)
  const isLocked = useMemo(() => {
    if (!editingBriefing) return false;
    if (isSuperuser) return false;
    
    // Check both createdAt and created_at fields
    const createdAtString = editingBriefing.createdAt || editingBriefing.created_at;
    if (!createdAtString) return false;
    
    const createdTime = new Date(createdAtString).getTime();
    const now = Date.now();
    const fourHoursMs = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
    
    return (now - createdTime) > fourHoursMs;
  }, [editingBriefing, isSuperuser]);

  // Handle superuser login
  const handleSuperuserLogin = () => {
    if (superuserPassword === "diamond") {
      setIsSuperuser(true);
      setShowSuperuserDialog(false);
      setSuperuserPassword("");
      toast.success("Superuser access granted");
    } else {
      toast.error("Invalid password");
      setSuperuserPassword("");
    }
  };

  // Use saved weather data when loaded from library, otherwise use live props
  const effectiveMetar = isLoadedFromLibrary && savedWeatherData?.metar ? savedWeatherData.metar : metar;
  const effectiveTaf = isLoadedFromLibrary && savedWeatherData?.taf ? savedWeatherData.taf : taf;
  const effectiveExplanation = isLoadedFromLibrary && savedWeatherData?.explanation ? savedWeatherData.explanation : explanation;
  const effectiveFlightCategory = isLoadedFromLibrary && savedWeatherData?.flightCategory ? savedWeatherData.flightCategory : flightCategory;
  const effectiveMultiAirportData = isLoadedFromLibrary && savedWeatherData?.multiAirportWeather?.length > 0 
    ? savedWeatherData.multiAirportWeather 
    : multiAirportData;

  // Handler to hide an airport from Weather Data section
  const handleHideWeatherAirport = (icao) => {
    setHiddenWeatherAirports(prev => [...prev, icao]);
  };

  // Handler to hide single airport
  const handleHideSingleAirport = () => {
    setHiddenSingleAirport(true);
  };

  // Load editing briefing data
  useEffect(() => {
    if (editingBriefing) {
      setFormData(editingBriefing.formData || {});
      setBriefingTitle(editingBriefing.title || "");
      // Load creation timestamp from saved briefing
      if (editingBriefing.createdAt) {
        setBriefingCreatedAt(editingBriefing.createdAt);
      } else if (editingBriefing.savedWeatherData?.savedAt) {
        setBriefingCreatedAt(editingBriefing.savedWeatherData.savedAt);
      }
      // Load saved weather data snapshot if available
      if (editingBriefing.savedWeatherData) {
        setSavedWeatherData(editingBriefing.savedWeatherData);
        setIsLoadedFromLibrary(true);
        // Restore route map if saved
        if (editingBriefing.savedWeatherData.routeMapImage) {
          setRouteMapImage(editingBriefing.savedWeatherData.routeMapImage);
        }
        // Restore radar image if saved
        if (editingBriefing.savedWeatherData.radarImageBase64) {
          setRadarImageBase64(editingBriefing.savedWeatherData.radarImageBase64);
        }
        // Restore Windy Maps GIFs if saved
        if (editingBriefing.savedWeatherData.windyMapsGifs) {
          setWindyMapsData({
            visible: true,
            gifs: editingBriefing.savedWeatherData.windyMapsGifs
          });
        }
        // Restore weather prediction data if saved
        if (editingBriefing.savedWeatherData.weatherPrediction) {
          setWeatherPredictionData(editingBriefing.savedWeatherData.weatherPrediction);
        }
        // Restore Windy snapshots if saved
        if (editingBriefing.savedWeatherData.windySnapshots && editingBriefing.savedWeatherData.windySnapshots.length > 0) {
          setWindySnapshots(editingBriefing.savedWeatherData.windySnapshots);
        }
      }
    } else {
      // Reset when not editing - set new creation timestamp
      setSavedWeatherData(null);
      setIsLoadedFromLibrary(false);
      setBriefingCreatedAt(new Date().toISOString());
    }
  }, [editingBriefing]);

  // Function to fetch NOTAMs for route airports
  const fetchRouteNotams = async () => {
    // Collect all airports to fetch NOTAMs for
    const airportsToFetch = new Set();
    
    // Use filtered weather data from selected routes only (if available)
    const activeWeatherData = weatherPredictionData?.filteredWeatherData || weatherPredictionData?.weatherData || [];
    
    // Add airports from active/filtered weather data (selected routes only)
    if (activeWeatherData.length > 0) {
      activeWeatherData.forEach(airport => {
        if (airport.icao) airportsToFetch.add(airport.icao.toUpperCase());
      });
    }
    
    // Fallback: Fetch airports from the FTX API directly if no weather data
    if (airportsToFetch.size === 0 && formData.ftxDesignator && formData.ftxDesignator !== 'NONE') {
      try {
        const ftxResponse = await fetch(`${API}/admin/ftx/${encodeURIComponent(formData.ftxDesignator)}`);
        if (ftxResponse.ok) {
          const ftxData = await ftxResponse.json();
          if (ftxData.airports && ftxData.airports.length > 0) {
            ftxData.airports.forEach(airport => {
              const icao = typeof airport === 'string' ? airport : airport.icao;
              if (icao) airportsToFetch.add(icao.toUpperCase());
            });
            console.log('fetchRouteNotams: Loaded airports from FTX API:', Array.from(airportsToFetch));
          }
        }
      } catch (error) {
        console.error('Failed to fetch FTX data for NOTAMs:', error);
      }
    }
    
    // Add multi-airport selection
    if (multiAirportData?.length > 0) {
      multiAirportData.forEach(airport => {
        if (airport.icao) airportsToFetch.add(airport.icao.toUpperCase());
      });
    }
    
    // Add single ICAO if entered
    if (icao && icao.length === 4) {
      airportsToFetch.add(icao.toUpperCase());
    }

    if (airportsToFetch.size === 0) {
      setNotams({});
      setNotamsLoading(false);
      return;
    }

    setNotamsLoading(true);
    console.log('fetchRouteNotams: Starting fetch for airports:', Array.from(airportsToFetch));
    try {
      // European/Mediterranean prefixes that use the EU NOTAM service
      // EB=Belgium, EL=Luxembourg, EH=Netherlands, ED=Germany, LF=France, EG=UK, EI=Ireland
      // LE=Spain, LP=Portugal, LI=Italy, LO=Austria, LS=Switzerland, EN=Norway, ES=Sweden, EK=Denmark, BI=Iceland
      // LX=Gibraltar, GM=Morocco, DA=Algeria, DT=Tunisia, LM=Malta, LC=Cyprus, LG=Greece
      const europeanPrefixes = ['EB', 'EL', 'EH', 'ED', 'LF', 'EG', 'EI', 'LE', 'LP', 'LI', 'LO', 'LS', 'EN', 'ES', 'EK', 'BI', 'LX', 'GM', 'DA', 'DT', 'LM', 'LC', 'LG'];
      
      // Fetch NOTAMs for all airports in parallel
      const notamResults = {};
      const fetchPromises = Array.from(airportsToFetch).map(async (airportIcao) => {
        try {
          const isEuropean = europeanPrefixes.some(prefix => airportIcao.startsWith(prefix));
          const endpoint = isEuropean 
            ? `${API}/weather/notams-eu/${airportIcao}?include_fir=false` 
            : `${API}/weather/notams/${airportIcao}`;
          console.log(`fetchRouteNotams: Fetching ${airportIcao} from ${endpoint}`);
          const response = await fetch(endpoint);
          const data = await response.json();
          console.log(`fetchRouteNotams: Got ${data.notams?.length || 0} NOTAMs for ${airportIcao}`);
          if (data.notams && data.notams.length > 0) {
            notamResults[airportIcao] = data.notams;
          }
        } catch (error) {
          console.error(`Failed to fetch NOTAMs for ${airportIcao}:`, error);
        }
      });
      
      await Promise.all(fetchPromises);
      console.log('fetchRouteNotams: Setting notams:', Object.keys(notamResults));
      setNotams(notamResults);
      
      // Fetch AI explanations if we have NOTAMs
      if (Object.keys(notamResults).length > 0) {
        console.log('fetchRouteNotams: Starting AI explanation fetch');
        setNotamExplanationsLoading(true);
        try {
          // Add timeout to prevent hanging
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
          
          const explainResponse = await fetch(`${API}/weather/notams/explain`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ notams: notamResults }),
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          
          const explainData = await explainResponse.json();
          console.log('fetchRouteNotams: AI explanation response:', explainData?.explanations ? 'received' : 'empty');
          if (explainData.explanations) {
            setNotamExplanations(explainData.explanations);
          }
        } catch (explainError) {
          if (explainError.name === 'AbortError') {
            console.error("NOTAM explanation request timed out");
          } else {
            console.error("Failed to explain NOTAMs:", explainError);
          }
        } finally {
          console.log('fetchRouteNotams: Setting notamExplanationsLoading to false');
          setNotamExplanationsLoading(false);
        }
      } else {
        console.log('fetchRouteNotams: No NOTAMs to explain');
      }
    } catch (error) {
      console.error("Failed to fetch NOTAMs:", error);
      setNotams({});
    } finally {
      console.log('fetchRouteNotams: Setting notamsLoading to false');
      setNotamsLoading(false);
    }
  };

  // Handle checkbox change with direct fetch
  const handleIncludeNotamsChange = (checked) => {
    setIncludeNotams(checked);
    if (checked) {
      fetchRouteNotams();
    } else {
      setNotams({});
      setNotamsLoading(false);
    }
  };

  // Auto-fetch NOTAMs when weather data is loaded AND checkbox is checked
  useEffect(() => {
    console.log('NOTAMs useEffect triggered:', {
      includeNotams,
      hasWeatherData: !!weatherPredictionData?.filteredWeatherData?.length,
      weatherDataLength: weatherPredictionData?.filteredWeatherData?.length || 0
    });
    if (includeNotams && weatherPredictionData?.filteredWeatherData?.length > 0) {
      // Weather data is now available, fetch NOTAMs
      console.log('Triggering fetchRouteNotams from useEffect');
      fetchRouteNotams();
    }
  }, [includeNotams, weatherPredictionData?.filteredWeatherData]);

  // Helper functions for dismissing/restoring NOTAMs
  const extractNotamId = (notamText) => {
    const match = notamText.match(/^([A-Z]\d{4}\/\d{2})/);
    return match ? match[1] : notamText.substring(0, 50); // Fallback to first 50 chars as ID
  };

  const dismissNotam = (airportIcao, notamIndex, notamText) => {
    const notamId = `${airportIcao}-${extractNotamId(notamText)}`;
    setDismissedNotams(prev => new Set([...prev, notamId]));
  };

  const dismissAirportNotams = (airportIcao) => {
    const airportNotams = notams[airportIcao] || [];
    setDismissedNotams(prev => {
      const newSet = new Set(prev);
      airportNotams.forEach((notam, index) => {
        const notamId = `${airportIcao}-${extractNotamId(notam)}`;
        newSet.add(notamId);
      });
      return newSet;
    });
  };

  const restoreNotam = (notamId) => {
    setDismissedNotams(prev => {
      const newSet = new Set(prev);
      newSet.delete(notamId);
      return newSet;
    });
  };

  const restoreAllNotams = () => {
    setDismissedNotams(new Set());
  };

  // Get filtered NOTAMs (excluding dismissed ones)
  const getFilteredNotams = () => {
    const filtered = {};
    Object.entries(notams).forEach(([airportIcao, airportNotams]) => {
      const filteredNotams = airportNotams.filter((notam, index) => {
        const notamId = `${airportIcao}-${extractNotamId(notam)}`;
        return !dismissedNotams.has(notamId);
      });
      if (filteredNotams.length > 0) {
        filtered[airportIcao] = filteredNotams;
      }
    });
    return filtered;
  };

  // Get filtered explanations (matching filtered NOTAMs)
  const getFilteredExplanations = () => {
    const filtered = {};
    Object.entries(notamExplanations).forEach(([airportIcao, explanation]) => {
      if (!explanation) return;
      const filteredItems = explanation.items?.filter(item => {
        const notamId = `${airportIcao}-${item.id}`;
        return !dismissedNotams.has(notamId);
      });
      if (filteredItems?.length > 0 || explanation.summary) {
        filtered[airportIcao] = {
          ...explanation,
          items: filteredItems || []
        };
      }
    });
    return filtered;
  };

  // Preload logo from public folder and convert to base64
  useEffect(() => {
    const loadLogo = async () => {
      try {
        // Load from public folder (same origin, no CORS issues)
        const response = await fetch('/nsas-logo.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Failed to load logo:", error);
      }
    };
    loadLogo();
  }, []);

  // Listen for reset event from logo click
  useEffect(() => {
    const handleReset = () => {
      // Reset all form data
      setFormData({
        ftxDesignator: "",
        registration: "",
        aircraftType: "",
        callsign: "",
        crewPIC: "",
        crewPM: "",
        crewSensorOperator: "",
        crewLiaisonOfficer: "",
        missionWindowTiming: "",
        missionWeather: "",
        poorVisibility: false,
        lowClouds: false,
        crosswind: false,
        thunderstorm: false,
        icingConditions: false,
        crewRecommendations: "",
        additionalNotes: ""
      });
      setBriefingTitle("");
      setHiddenWeatherAirports([]);
      setHiddenSingleAirport(false);
      setSavedWeatherData(null);
      setIsLoadedFromLibrary(false);
      setBriefingCreatedAt(new Date().toISOString());
      setRouteMapImage(null);
      setRadarImage(null);
      setRadarImageBase64(null);
      setShowSaveDialog(false);
    };

    window.addEventListener('resetBriefingForm', handleReset);
    return () => window.removeEventListener('resetBriefingForm', handleReset);
  }, []);

  // Fetch radar image when ICAO changes
  useEffect(() => {
    const fetchRadarImage = async () => {
      if (!icao) {
        setRadarImage(null);
        setRadarImageBase64(null);
        return;
      }

      setRadarLoading(true);
      try {
        // Get RainViewer radar data
        const apiResponse = await fetch('https://api.rainviewer.com/public/weather-maps.json');
        const data = await apiResponse.json();
        
        if (data.radar && data.radar.past && data.radar.past.length > 0) {
          // Get the most recent radar frame
          const latestFrame = data.radar.past[data.radar.past.length - 1];
          const coords = getAirportCoords(icao);
          
          // Calculate tile coordinates for zoom level 6
          const zoom = 6;
          const n = Math.pow(2, zoom);
          const x = Math.floor((coords.lon + 180) / 360 * n);
          const y = Math.floor((1 - Math.log(Math.tan(coords.lat * Math.PI / 180) + 1 / Math.cos(coords.lat * Math.PI / 180)) / Math.PI) / 2 * n);
          
          // OpenStreetMap base tile URL
          const osmUrl = `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
          // RainViewer radar overlay URL (transparent PNG)
          const radarUrl = `https://tilecache.rainviewer.com${latestFrame.path}/256/${zoom}/${x}/${y}/2/1_1.png`;
          
          // Create composite image using canvas
          const canvas = document.createElement('canvas');
          canvas.width = 256;
          canvas.height = 256;
          const ctx = canvas.getContext('2d');
          
          // Load both images
          const loadImage = (url) => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => resolve(img);
              img.onerror = reject;
              img.src = url;
            });
          };
          
          try {
            // Load base map
            const osmImg = await loadImage(osmUrl);
            ctx.drawImage(osmImg, 0, 0, 256, 256);
            
            // Load and overlay radar (with transparency)
            const radarImg = await loadImage(radarUrl);
            ctx.globalAlpha = 0.7; // Semi-transparent radar overlay
            ctx.drawImage(radarImg, 0, 0, 256, 256);
            ctx.globalAlpha = 1.0;
            
            // Add a small marker for the airport location
            const markerX = 128; // Center of tile (approximate)
            const markerY = 128;
            ctx.fillStyle = '#ef4444';
            ctx.beginPath();
            ctx.arc(markerX, markerY, 5, 0, 2 * Math.PI);
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.stroke();
            
            // Convert canvas to base64
            const compositeBase64 = canvas.toDataURL('image/png');
            
            setRadarImage({
              url: compositeBase64,
              timestamp: new Date(latestFrame.time * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
              coords: coords
            });
            setRadarImageBase64(compositeBase64);
            
          } catch (imgError) {
            console.error("Failed to create composite image:", imgError);
            // Fallback to radar-only image
            const mapUrl = `https://tilecache.rainviewer.com${latestFrame.path}/512/${zoom}/${x}/${y}/4/1_1.png`;
            setRadarImage({
              url: mapUrl,
              timestamp: new Date(latestFrame.time * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
              coords: coords
            });
            
            try {
              const imageResponse = await fetch(mapUrl);
              const blob = await imageResponse.blob();
              const reader = new FileReader();
              reader.onloadend = () => {
                setRadarImageBase64(reader.result);
              };
              reader.readAsDataURL(blob);
            } catch (e) {
              setRadarImageBase64(null);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch radar data:", error);
        setRadarImage(null);
        setRadarImageBase64(null);
      } finally {
        setRadarLoading(false);
      }
    };

    fetchRadarImage();
  }, [icao]);

  // Listen for download triggers from library
  useEffect(() => {
    const handleDownloadTrigger = async (event) => {
      const { format } = event.detail;
      
      // Wait for logo to load (max 3 seconds)
      let waitTime = 0;
      while (!logoBase64 && waitTime < 3000) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitTime += 100;
      }
      
      // Additional small delay to ensure all components are rendered
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (format === 'pdf') {
        handleDownloadPDF();
      } else if (format === 'html') {
        handleDownloadHTML();
      }
    };
    
    window.addEventListener('triggerBriefingDownload', handleDownloadTrigger);
    return () => {
      window.removeEventListener('triggerBriefingDownload', handleDownloadTrigger);
    };
  }, [formData, effectiveMetar, effectiveTaf, icao, effectiveFlightCategory, effectiveMultiAirportData, isMultiAirport, logoBase64]);

  const handleInputChange = (field, value) => {
    // If FTX designator changes, reset the form first
    if (field === 'ftxDesignator' && value !== formData.ftxDesignator) {
      // Reset all form data except the new FTX designator
      setFormData({
        ftxDesignator: value,
        registration: "",
        aircraftType: "",
        callsign: "",
        missionWeather: "",
        crewRecommendations: "",
        crewPIC: "",
        crewPM: "",
        crewLiaisonOfficer: "",
        crewSensorOperator: "",
        missionWindowTiming: "",
        poorVisibility: false,
        lowClouds: false,
        crosswind: false,
        thunderstorm: false,
        icingConditions: false,
      });
      
      // Reset other related states
      setIncludeNotams(false);
      setNotams({});
      setNotamExplanations({});
      setNotStandardRouting(false);
      setWeatherPredictionData(null);
      setWindySnapshots([]);
      setRouteMapImage(null);
      setHiddenWeatherAirports([]);
      setSelectedSortie([0]);
      setSkylegsData(null);
      
      // Fetch SkyLegs data for the new FTX designator with today's date (since we just reset the form)
      const today = new Date().toISOString().split('T')[0];
      if (value && value !== 'NONE') {
        fetchSkylegsFlightData(value, today);
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Fetch SkyLegs data when FTX designator changes (fallback, shouldn't reach here normally)
    if (field === 'ftxDesignator' && value) {
      fetchSkylegsFlightData(value, formData.missionDate);
    }
  };

  // Auto-format European callsigns by adding hyphen after first 2 characters
  // European registrations follow format: XX-XXX (e.g., OO-SNA, G-ABCD, D-EABC, F-GXXX)
  // American callsigns start with N and don't use hyphens (e.g., N12345, N789AB)
  const formatCallsign = (value) => {
    const cleaned = value.replace(/-/g, '').toUpperCase();
    
    // American callsigns start with N - no hyphen needed
    if (cleaned.startsWith('N')) {
      return cleaned;
    }
    
    // European callsigns: insert hyphen after position 2 (or 1 for UK G-XXXX)
    if (cleaned.length > 2) {
      // UK registrations start with G and use format G-XXXX
      if (cleaned.startsWith('G')) {
        return cleaned.slice(0, 1) + '-' + cleaned.slice(1);
      }
      // Other European: XX-XXX format
      return cleaned.slice(0, 2) + '-' + cleaned.slice(2);
    }
    return cleaned;
  };

  const handleCallsignChange = (e) => {
    const formatted = formatCallsign(e.target.value);
    handleInputChange('callsign', formatted);
  };

  // Auto-select aircraft type based on registration
  const handleRegistrationChange = (value) => {
    handleInputChange('registration', value);
    
    // Auto-select aircraft type based on registration
    // OO-MSC → DA42 MPP
    // All other registrations → DA62 MPP
    
    // Find the correct aircraft type value from AIRCRAFT_TYPES
    const da42Type = AIRCRAFT_TYPES.find(ac => 
      ac.label.toLowerCase().includes('da42') || ac.value.includes('da42')
    );
    const da62Type = AIRCRAFT_TYPES.find(ac => 
      ac.label.toLowerCase().includes('da62') || ac.value.includes('da62')
    );
    
    if (value === 'OO-MSC' && da42Type) {
      handleInputChange('aircraftType', da42Type.value);
    } else if (value && da62Type) {
      // Default to DA62 MPP for all other registrations
      handleInputChange('aircraftType', da62Type.value);
    }
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getCurrentDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getAircraftTypeName = (type) => {
    // First check dynamic AIRCRAFT_TYPES
    const found = AIRCRAFT_TYPES.find(ac => ac.value === type);
    if (found) return found.label;
    
    // Fallback to static mapping
    const types = {
      da42mpp: "Diamond DA42 MPP",
      da62mpp: "Diamond DA62 MPP",
    };
    return types[type] || type || "-";
  };

  const getFtxDesignatorName = (value) => {
    const designators = {
      "SPARROW 3": "SPARROW 3",
      "OSPREY 3": "OSPREY 3",
      sparrow3: "SPARROW 3",
      osprey3: "OSPREY 3",
    };
    return designators[value] || value || "-";
  };

  // Route airports mapping by FTX designator
  const FTX_ROUTE_AIRPORTS = {
    "SPARROW 3": ["EBOS", "LFAC", "LFAT"],
    "sparrow3": ["EBOS", "LFAC", "LFAT"],
  };

  // Get route string for FTX designator
  const getRouteString = (ftxDesignator) => {
    const route = FTX_ROUTE_AIRPORTS[ftxDesignator];
    if (route && route.length > 0) {
      return route.join('-');
    }
    return icao || 'UNKNOWN';
  };

  // Get all visible airport ICAOs for display
  const getAllVisibleAirports = () => {
    const airports = [];
    
    // Add route airports FIRST if SPARROW 3 is selected
    if (formData.ftxDesignator === "SPARROW 3") {
      SPARROW3_ROUTE_AIRPORTS.forEach(airport => airports.push(airport.icao));
    }
    
    // Add searched airports (single or multi) that aren't already in the list
    if (isMultiAirport && effectiveMultiAirportData?.length > 0) {
      effectiveMultiAirportData.forEach(airport => {
        if (!hiddenWeatherAirports.includes(airport.icao) && !airports.includes(airport.icao)) {
          airports.push(airport.icao);
        }
      });
    } else if (icao && !hiddenSingleAirport && !airports.includes(icao)) {
      airports.push(icao);
    }
    
    return airports;
  };

  // Generate filename for downloads
  const getDownloadFilename = (extension) => {
    const ftxName = (formData.ftxDesignator || 'UNKNOWN').replace(/\s+/g, '');
    const routeStr = getRouteString(formData.ftxDesignator);
    const now = new Date();
    const dateTime = now.toISOString().replace(/[-:]/g, '').replace('T', '_').slice(0, 15);
    return `Wx_NSAS_${ftxName}_${routeStr}_${dateTime}.${extension}`;
  };

  // Route airports for SPARROW 3
  const SPARROW3_ROUTE_AIRPORTS = [
    { icao: "EBOS", name: "Ostend-Bruges", lat: 51.1989, lon: 2.8622 },
    { icao: "LFAC", name: "Calais-Dunkerque", lat: 50.9617, lon: 1.9547 },
    { icao: "LFAT", name: "Le Touquet", lat: 50.5175, lon: 1.6306 }
  ];

  // SPARROW 3 Route coordinates for static map
  const SPARROW3_ROUTE_COORDS = [
    [51.19993399999999, 2.875865],
    [51.084722, 2.55],
    [51.0375, 2.384722],
    [50.97, 2.169722],
    [50.91916700000001, 1.791389],
    [50.848056, 1.644444],
    [50.784444, 1.655278],
    [50.71277800000001, 1.638056],
    [50.478611, 1.633333],
    [50.249444, 1.605833],
    [50.271111, 1.546111],
    [50.558611, 1.588889],
    [50.69277799999999, 1.580278],
    [50.769444, 1.614722],
    [50.855278, 1.602222],
    [50.946944, 1.795833],
    [50.978611, 1.969722],
    [50.965278, 2.155278],
    [51.018889, 2.278056],
    [51.081111, 2.551944],
    [51.199593, 2.872677]
  ];

  // Generate route weather HTML for PDF/HTML reports using weatherPredictionData
  const generateRouteWeatherHtml = (staticMapImage = null, isHtml = false, selectedRouteNames = [], routesData = []) => {
    if (!weatherPredictionData || notStandardRouting) {
      return isHtml ? { html: '', script: '' } : '';
    }

    const wp = weatherPredictionData;
    const risk = wp.risk || 'LOW';
    const ftxName = formData.ftxDesignator || 'Flight Route';
    const routeName = wp.routeName || ftxName;
    const timeWindow = wp.timeWindow || `${wp.missionStartTime}Z - ${wp.missionEndTime}Z`;
    const weatherData = wp.filteredWeatherData || wp.weatherData || [];
    
    // Get route info - always show airport ICAO codes
    let routeDescription = '';
    if (weatherData.length > 0) {
      const airports = weatherData.map(a => a.icao).join(' → ');
      routeDescription = airports;
    } else if (wp.weatherData && wp.weatherData.length > 0) {
      // Fallback to all weather data if filtered is empty
      const airports = wp.weatherData.map(a => a.icao).join(' → ');
      routeDescription = airports;
    }

    // Build AI prediction HTML
    let aiPredictionHtml = '';
    if (showAIPrediction && wp) {
      const missionSafety = wp.missionSafety || 'GO';
      const patrolEffectiveness = wp.patrolEffectiveness || 'EFFECTIVE';
      const fl = wp.flightLimitations || {};
      const tafTimeWindow = wp.tafTimeWindow || timeWindow;
      
      // Build Analysis Summary text
      let analysisSummary = '';
      if (missionSafety === 'NO GO') {
        analysisSummary = '<span style="font-weight: 600; color: #dc2626;">Flight not recommended.</span> ';
        if (fl.knownIcingConditions) analysisSummary += 'Known icing conditions present due to precipitation with temperatures near freezing. ';
        if (fl.poorVisibility || fl.lowClouds) analysisSummary += 'Reduced visibility and/or low cloud base forecast along the route compromises VFR operations. ';
        if (wp.tafHasIFR) analysisSummary += 'TAF indicates IFR conditions expected during the mission window. ';
        if (fl.crosswindExceeds20) analysisSummary += 'Crosswind component exceeds the 20KT operational limit. ';
        if (fl.thunderstormCB) analysisSummary += 'Thunderstorm activity or cumulonimbus clouds reported/forecast in the area. ';
        analysisSummary += 'VFR patrol cannot be conducted safely. IFR capability is reserved for abort/return to base only. Recommend postponing mission until conditions improve.';
      } else if (missionSafety === 'GO WITH CAUTION') {
        analysisSummary = '<span style="font-weight: 600; color: #b45309;">Proceed with caution.</span> ';
        if (fl.lowClouds) analysisSummary += 'Cloud base may require altitude adjustments during the patrol. ';
        if (fl.poorVisibility) analysisSummary += 'Visibility conditions may limit patrol effectiveness. ';
        analysisSummary += 'Maintain awareness of changing conditions and be prepared for early return if needed.';
      } else {
        analysisSummary = '<span style="font-weight: 600; color: #047857;">Conditions favorable for flight.</span> ';
        analysisSummary += 'Weather parameters within acceptable limits for VFR patrol operations. Standard pre-flight weather monitoring advised.';
      }
      
      aiPredictionHtml = `
        <div class="weather-analysis-section" style="margin: 15px 0; padding: 14px; background: ${risk === 'HIGH' ? '#fef2f2' : risk === 'MODERATE' ? '#fffbeb' : '#f0fdf4'}; border: 1px solid ${risk === 'HIGH' ? '#fecaca' : risk === 'MODERATE' ? '#fde68a' : '#bbf7d0'}; border-radius: 6px;">
          
          <!-- Header -->
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid ${risk === 'HIGH' ? '#fecaca' : risk === 'MODERATE' ? '#fde68a' : '#bbf7d0'};">
            <span style="font-size: 14px;">🕐</span>
            <span style="font-weight: 600; font-size: 12px; color: #1e293b;">Weather Analysis by AI</span>
            <span style="font-size: 10px; color: #64748b;">${timeWindow}</span>
          </div>
          
          <!-- 2x2 Card Grid -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px;">
            
            <!-- Card 1: TAF Forecast -->
            <div style="background: rgba(255,255,255,0.8); border-radius: 6px; padding: 10px; border: 1px solid #e2e8f0;">
              <h6 style="font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; margin: 0 0 8px 0;">
                TAF Forecast <span style="font-weight: 400; color: #94a3b8;">(${tafTimeWindow})</span>
              </h6>
              ${wp.tafForecastByAirport && wp.tafForecastByAirport.length > 0 ? 
                wp.tafForecastByAirport.map(apt => `
                  <div style="border-bottom: 1px solid #f1f5f9; padding-bottom: 6px; margin-bottom: 6px;">
                    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <span style="font-weight: 700; font-size: 11px; color: #334155;">${apt.icao}</span>
                        <span style="font-size: 9px; color: #94a3b8;">${apt.name || ''}</span>
                      </div>
                      ${apt.hasMissionWindowData !== false && apt.category ? `
                        <span style="padding: 2px 6px; border-radius: 3px; font-size: 9px; font-weight: 600; background: ${
                          apt.category === 'VFR' ? '#d1fae5' : apt.category === 'MVFR' ? '#fef3c7' : apt.category === 'IFR' ? '#fee2e2' : '#fce7f3'
                        }; color: ${
                          apt.category === 'VFR' ? '#047857' : apt.category === 'MVFR' ? '#b45309' : apt.category === 'IFR' ? '#dc2626' : '#be185d'
                        };">${apt.category}</span>
                      ` : ''}
                    </div>
                    ${apt.hasMissionWindowData === false ? `
                      <div style="font-size: 8px; color: #d97706; background: #fffbeb; padding: 3px 6px; border-radius: 3px;">
                        ⚠ TAF does not cover mission window (${tafTimeWindow})
                      </div>
                    ` : `
                      <div style="display: flex; flex-wrap: wrap; gap: 8px; font-size: 9px; color: #475569;">
                        ${apt.visibility ? `<span ${apt.visibility < 5000 ? 'style="color: #dc2626; font-weight: 500;"' : ''}>Vis: ${apt.visibility >= 9999 ? '10km+' : apt.visibility + 'm'}</span>` : ''}
                        ${apt.ceiling ? `<span ${apt.ceiling.alt < 1500 ? 'style="color: #dc2626; font-weight: 500;"' : ''}>${apt.ceiling.type}@${apt.ceiling.alt}ft</span>` : ''}
                        ${apt.wind ? `<span>${apt.wind.dir}°/${apt.wind.speed}${apt.wind.gusts ? '<span style="color: #d97706; font-weight: 600;">G' + apt.wind.gusts + '</span>' : ''}KT</span>` : ''}
                      </div>
                      ${apt.issues && apt.issues.length > 0 ? `<div style="margin-top: 4px; font-size: 8px; color: #dc2626;">${apt.issues.map(i => '⚠ ' + i).join(' ')}</div>` : ''}
                    `}
                  </div>
                `).join('') : '<div style="font-size: 10px; color: #64748b;">No TAF data available</div>'}
            </div>
            
            <!-- Card 2: Flight Limitations -->
            <div style="background: rgba(255,255,255,0.8); border-radius: 6px; padding: 10px; border: 1px solid #e2e8f0;">
              <h6 style="font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; margin: 0 0 8px 0;">
                Flight Limitations
              </h6>
              ${wp.tafHasIFR ? '<div style="font-size: 10px; color: #dc2626; font-weight: 600; margin-bottom: 6px;">⚠ IFR Conditions Expected</div>' : ''}
              <div style="display: flex; flex-direction: column; gap: 3px; font-size: 10px;">
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="color: ${fl.poorVisibility ? '#dc2626' : '#94a3b8'};">${fl.poorVisibility ? '☑' : '☐'}</span>
                  <span style="color: ${fl.poorVisibility ? '#b91c1c' : '#64748b'}; ${fl.poorVisibility ? 'font-weight: 500;' : ''}">Poor visibility</span>
                </div>
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="color: ${fl.lowClouds ? '#dc2626' : '#94a3b8'};">${fl.lowClouds ? '☑' : '☐'}</span>
                  <span style="color: ${fl.lowClouds ? '#b91c1c' : '#64748b'}; ${fl.lowClouds ? 'font-weight: 500;' : ''}">Low clouds</span>
                </div>
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="color: ${fl.crosswindExceeds20 ? '#dc2626' : '#94a3b8'};">${fl.crosswindExceeds20 ? '☑' : '☐'}</span>
                  <span style="color: ${fl.crosswindExceeds20 ? '#b91c1c' : '#64748b'}; ${fl.crosswindExceeds20 ? 'font-weight: 500;' : ''}">XW &gt;20KT</span>
                </div>
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="color: ${fl.thunderstormCB ? '#dc2626' : '#94a3b8'};">${fl.thunderstormCB ? '☑' : '☐'}</span>
                  <span style="color: ${fl.thunderstormCB ? '#b91c1c' : '#64748b'}; ${fl.thunderstormCB ? 'font-weight: 500;' : ''}">TS/CB</span>
                </div>
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="color: ${fl.knownIcingConditions ? '#dc2626' : '#94a3b8'};">${fl.knownIcingConditions ? '☑' : '☐'}</span>
                  <span style="color: ${fl.knownIcingConditions ? '#b91c1c' : '#64748b'}; ${fl.knownIcingConditions ? 'font-weight: 500;' : ''}">Icing</span>
                </div>
              </div>
            </div>
            
            <!-- Card 3: Mission Safety -->
            <div style="border-radius: 6px; padding: 10px; border: 1px solid ${missionSafety === 'GO' ? '#86efac' : missionSafety === 'GO WITH CAUTION' ? '#fde68a' : '#fecaca'}; background: ${missionSafety === 'GO' ? '#dcfce7' : missionSafety === 'GO WITH CAUTION' ? '#fef3c7' : '#fee2e2'};">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                <h6 style="font-size: 10px; font-weight: 700; text-transform: uppercase; margin: 0;">Mission Safety</h6>
                <span style="font-size: 14px; color: ${missionSafety === 'GO' ? '#047857' : missionSafety === 'GO WITH CAUTION' ? '#b45309' : '#dc2626'};">
                  ${missionSafety === 'GO' ? '✓' : missionSafety === 'GO WITH CAUTION' ? '⚠' : '✗'}
                </span>
              </div>
              <div style="font-size: 12px; font-weight: 700; color: ${missionSafety === 'GO' ? '#047857' : missionSafety === 'GO WITH CAUTION' ? '#b45309' : '#dc2626'}; margin-bottom: 6px;">
                ${missionSafety}
              </div>
              ${wp.missionSafetyReasons && wp.missionSafetyReasons.length > 0 ? `
                <div style="font-size: 9px; color: #475569;">
                  ${wp.missionSafetyReasons.map(r => '<div style="margin-bottom: 2px;">• ' + r + '</div>').join('')}
                </div>
              ` : ''}
            </div>
            
            <!-- Card 4: Patrol Effectiveness -->
            <div style="border-radius: 6px; padding: 10px; border: 1px solid ${patrolEffectiveness === 'EFFECTIVE' ? '#86efac' : patrolEffectiveness === 'REDUCED' ? '#fde68a' : '#fecaca'}; background: ${patrolEffectiveness === 'EFFECTIVE' ? '#dcfce7' : patrolEffectiveness === 'REDUCED' ? '#fef3c7' : '#fee2e2'};">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                <h6 style="font-size: 10px; font-weight: 700; text-transform: uppercase; margin: 0;">Patrol Outcome</h6>
                <span style="font-size: 14px; color: ${patrolEffectiveness === 'EFFECTIVE' ? '#047857' : patrolEffectiveness === 'REDUCED' ? '#b45309' : '#dc2626'};">
                  ${patrolEffectiveness === 'EFFECTIVE' ? '✓' : patrolEffectiveness === 'REDUCED' ? '⚠' : '✗'}
                </span>
              </div>
              <div style="font-size: 12px; font-weight: 700; color: ${patrolEffectiveness === 'EFFECTIVE' ? '#047857' : patrolEffectiveness === 'REDUCED' ? '#b45309' : '#dc2626'}; margin-bottom: 6px;">
                ${patrolEffectiveness}
              </div>
              ${wp.patrolReasons && wp.patrolReasons.length > 0 ? `
                <div style="font-size: 9px; color: #475569;">
                  ${wp.patrolReasons.map(r => '<div style="margin-bottom: 2px;">• ' + r + '</div>').join('')}
                </div>
              ` : ''}
            </div>
          </div>
          
          <!-- Analysis Summary (Full Width) -->
          <div style="background: rgba(255,255,255,0.8); border-radius: 6px; padding: 10px; border: 1px solid #e2e8f0;">
            <h6 style="font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; margin: 0 0 8px 0;">Analysis Summary</h6>
            <p style="font-size: 10px; color: #475569; line-height: 1.5; margin: 0;">${analysisSummary}</p>
          </div>
          
        </div>
      `;
    }

    // Build enroute weather HTML (weather waypoints from OpenWeatherMap)
    let enrouteWeatherHtml = '';
    
    if (wp.routeWaypointWeather?.waypoints?.length > 0) {
      try {
        const waypoints = wp.routeWaypointWeather.waypoints;
        const assessment = wp.routeWaypointWeather.route_assessment || {};
        
        // Helper to convert decimal to DMS
        const toDMS = (decimal, isLat) => {
          const abs = Math.abs(decimal);
          const deg = Math.floor(abs);
          const minFloat = (abs - deg) * 60;
          const min = Math.floor(minFloat);
          const sec = Math.round((minFloat - min) * 60);
          const dir = isLat ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
          return deg + '°' + min.toString().padStart(2,'0') + "'" + sec.toString().padStart(2,'0') + '"' + dir;
        };
        
        const waypointsHtml = waypoints.slice(0, 6).map((wpItem, i) => {
          const catColor = wpItem.weather_summary?.category === 'VFR' ? '#047857' : wpItem.weather_summary?.category === 'MVFR' ? '#b45309' : '#dc2626';
          return `
            <div style="background: white; border: 1px solid #e2e8f0; border-radius: 4px; padding: 6px;">
              <div style="font-size: 9px; font-weight: 600; color: #0891b2; margin-bottom: 2px;">${i + 1}. ${wpItem.location_name || 'Wx #' + (i + 1)}</div>
              <div style="display: flex; align-items: center; gap: 4px; margin-bottom: 2px;">
                <span style="font-size: 9px; font-weight: 700; color: ${catColor};">${wpItem.weather_summary?.category || '?'}</span>
                <span style="font-size: 8px; color: #64748b;">${wpItem.weather_summary?.wind_kt?.toFixed(0) || 0}kt</span>
              </div>
              <div style="font-size: 7px; color: #94a3b8; font-family: monospace;">${toDMS(wpItem.lat, true)} ${toDMS(wpItem.lon, false)}</div>
            </div>
          `;
        }).join('');
        
        const assessBg = assessment.overall_category === 'VFR' ? '#d1fae5' : assessment.overall_category === 'MVFR' ? '#fef3c7' : '#fee2e2';
        const assessColor = assessment.overall_category === 'VFR' ? '#047857' : assessment.overall_category === 'MVFR' ? '#b45309' : '#dc2626';
        
        enrouteWeatherHtml = `
          <div style="background: #ecfeff; border: 1px solid #a5f3fc; border-radius: 6px; padding: 10px; margin-bottom: 10px;">
            <h6 style="font-size: 10px; font-weight: 700; color: #0891b2; text-transform: uppercase; margin: 0 0 8px 0;">
              Enroute Weather <span style="font-weight: 400; color: #64748b; margin-left: 8px;">Source: OpenWeatherMap API - ${waypoints.length} waypoints</span>
            </h6>
            
            <div style="background: ${assessBg}; padding: 6px 10px; border-radius: 4px; margin-bottom: 8px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
              <span style="font-size: 10px; font-weight: 700; color: ${assessColor};">
                ROUTE: ${assessment.overall_category || 'VFR'}
              </span>
              <span style="font-size: 9px; color: #64748b;">
                VFR:${assessment.vfr_waypoints || 0} | MVFR:${assessment.mvfr_waypoints || 0} | IFR:${assessment.ifr_waypoints || 0}
              </span>
              <span style="font-size: 9px; color: #64748b;">
                Max Wind: ${assessment.max_wind_kt?.toFixed(0) || 0}kt | Min Vis: ${assessment.min_visibility_km?.toFixed(0) || 10}km
              </span>
            </div>
            
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;">
              ${waypointsHtml}
            </div>
          </div>
        `;
      } catch (err) {
        console.error('[HTML Report] Error building enroute weather HTML:', err);
      }
    }

    // Build airports weather HTML
    const airportsHtml = weatherData.map(airport => `
      <div style="background: #f8fafc; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 3px; margin-bottom: 10px;">
        <div style="margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; font-size: 12px;">
          <strong>${airport.icao}</strong> - ${airport.name || getAirportName(airport.icao)}${airport.metar?.flight_category ? ` <span style="color: ${
            airport.metar.flight_category === 'VFR' ? '#047857' :
            airport.metar.flight_category === 'MVFR' ? '#1d4ed8' :
            airport.metar.flight_category === 'IFR' ? '#dc2626' :
            airport.metar.flight_category === 'LIFR' ? '#7c3aed' : '#475569'
          }; font-weight: bold;">(${airport.metar.flight_category})</span>` : ''}
        </div>
        ${airport.metar?.raw ? `
          <div style="margin-bottom: 6px;">
            <p style="margin: 0 0 3px 0; font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: bold;">METAR</p>
            <p style="margin: 0; font-family: monospace; font-size: 10px; color: #1e293b; line-height: 1.4;">${airport.metar.raw}</p>
          </div>
        ` : ''}
        ${airport.taf?.raw ? `
          <div>
            <p style="margin: 0 0 3px 0; font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: bold;">TAF</p>
            <p style="margin: 0; font-family: monospace; font-size: 10px; color: #1e293b; line-height: 1.4;">${airport.taf.raw}</p>
          </div>
        ` : ''}
      </div>
    `).join('');

    // Generate interactive Leaflet map HTML for HTML reports (or static image for PDF)
    let mapHtml = '';
    let mapScript = ''; // Script to be added at the end of body
    
    if (isHtml && routesData && routesData.length > 0) {
      // Calculate map center from all route coordinates
      const allCoords = routesData.flatMap(r => r.coordinates || []);
      const avgLat = allCoords.reduce((sum, c) => sum + c[0], 0) / allCoords.length;
      const avgLon = allCoords.reduce((sum, c) => sum + c[1], 0) / allCoords.length;
      
      // Route colors matching the live map
      const routeColors = ['#dc2626', '#2563eb', '#059669', '#7c3aed', '#ea580c'];
      
      // Generate route polylines JavaScript
      const routePolylinesJs = routesData.map((route, idx) => {
        const coords = (route.coordinates || []).map(c => `[${c[0]}, ${c[1]}]`).join(',');
        const color = routeColors[idx % routeColors.length];
        return `L.polyline([${coords}], {color: '${color}', weight: 3, opacity: 0.9}).addTo(map);`;
      }).join('\n          ');
      
      // Generate airport markers JavaScript
      const airportMarkersJs = weatherData.map(airport => {
        const cat = airport.metar?.flight_category || 'UNKNOWN';
        const color = cat === 'VFR' ? '#10b981' : cat === 'MVFR' ? '#3b82f6' : cat === 'IFR' ? '#ef4444' : cat === 'LIFR' ? '#8b5cf6' : '#64748b';
        return `L.circleMarker([${airport.lat}, ${airport.lon}], {radius: 8, fillColor: '${color}', color: '#fff', weight: 2, fillOpacity: 0.9}).addTo(map).bindPopup('<b>${airport.icao}</b><br>${airport.name || ''}<br><span style="color:${color};font-weight:bold">${cat}</span>');`;
      }).join('\n          ');
      
      // Map container with fallback static image
      mapHtml = `
        <div style="margin-bottom: 10px; width: 100%;">
          <!-- Interactive map container (will be populated by JavaScript if Leaflet loads) -->
          <div id="routeMap" style="width: 100%; height: 300px; border-radius: 4px; border: 1px solid #e2e8f0; display: none;"></div>
          <!-- Static map fallback (shown if Leaflet fails to load) -->
          <div id="staticMapFallback" style="width: 100%;">
            ${staticMapImage ? `<img src="${staticMapImage}" alt="Flight Route Map" style="width: 100%; max-width: none; height: auto; border-radius: 4px; border: 1px solid #e2e8f0; display: block;" />` : '<div style="width: 100%; height: 300px; background: #f1f5f9; border-radius: 4px; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #64748b;">Map loading...</div>'}
          </div>
          <p style="margin: 5px 0 0 0; font-size: 8px; color: #64748b;">Flight route: ${selectedRouteNames.join(', ') || routeName}</p>
        </div>
      `;
      
      // Script to load Leaflet and initialize map (added at end of body)
      mapScript = `
    <!-- Leaflet JS loaded at end of body for better compatibility -->
    <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
    <script>
      (function() {
        var retryCount = 0;
        var maxRetries = 50; // 5 seconds max wait
        
        function initRouteMap() {
          retryCount++;
          if (typeof L === 'undefined') {
            if (retryCount < maxRetries) {
              setTimeout(initRouteMap, 100);
            }
            return;
          }
          
          try {
            var mapContainer = document.getElementById('routeMap');
            var fallback = document.getElementById('staticMapFallback');
            if (!mapContainer) return;
            
            // Show interactive map, hide fallback
            mapContainer.style.display = 'block';
            if (fallback) fallback.style.display = 'none';
            
            var map = L.map('routeMap').setView([${avgLat}, ${avgLon}], 7);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap'
            }).addTo(map);
            
            ${routePolylinesJs}
            ${airportMarkersJs}
            
            // Add weather waypoint markers
            ${weatherPredictionData?.routeWaypointWeather?.waypoints?.map((wp, idx) => {
              const cat = wp.weather_summary?.category || 'VFR';
              const color = cat === 'VFR' ? '#10b981' : cat === 'MVFR' ? '#f59e0b' : '#ef4444';
              const name = wp.location_name || `Wx #${idx + 1}`;
              return `L.circleMarker([${wp.lat}, ${wp.lon}], {radius: 5, fillColor: '${color}', color: '#fff', weight: 1, fillOpacity: 0.9}).addTo(map).bindPopup('<b>${idx + 1}. ${name}</b><br><span style="color:${color};font-weight:bold">${cat}</span><br>Wind: ${wp.weather_summary?.wind_kt?.toFixed(0) || 0}kt');`;
            }).join('\n            ') || ''}
            
            var allCoords = [${allCoords.map(c => `[${c[0]}, ${c[1]}]`).join(',')}];
            if (allCoords.length > 0) {
              map.fitBounds(allCoords, {padding: [20, 20]});
            }
          } catch(e) {
            console.error('Map error:', e);
            // Keep static fallback visible on error
          }
        }
        
        if (document.readyState === 'complete') {
          initRouteMap();
        } else {
          window.addEventListener('load', initRouteMap);
        }
      })();
    <\/script>`;
    } else if (staticMapImage) {
      // Fallback to static image for PDF
      mapHtml = `
        <div style="margin-bottom: 10px; text-align: center; width: 100%;">
          <img src="${staticMapImage}" alt="Flight Route Map" style="width: 100%; max-width: none; height: auto; border-radius: 4px; border: 1px solid #e2e8f0; display: block;" />
          <p style="margin: 5px 0 0 0; font-size: 8px; color: #64748b;">Flight route: ${routeName}</p>
        </div>
      `;
    }

    const resultHtml = `
      <div style="margin-bottom: 18px; width: 100%;">
        <h2 style="font-size: 11px; color: #4f46e5; border-bottom: 1px solid #c7d2fe; padding-bottom: 5px; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px;">
          Flight Route ${routeName}
        </h2>
        ${mapHtml}
        ${aiPredictionHtml}
        ${enrouteWeatherHtml}
        <div style="background: #eef2ff; padding: 10px; border: 1px solid #c7d2fe; border-radius: 3px; margin-bottom: 10px;">
          <p style="margin: 0 0 5px 0; font-size: 9px; color: #4f46e5; font-weight: bold;">Flight route: ${routeDescription}</p>
          <p style="margin: 0; font-size: 9px; color: #1e293b;">Time Window: ${timeWindow}</p>
        </div>
        ${airportsHtml}
      </div>
    `;
    
    // Return object with script for HTML reports, plain string for PDF
    if (isHtml) {
      return { html: resultHtml, script: mapScript };
    }
    return resultHtml;
  };
  const handleDownloadPDF = async () => {
    // Validate mandatory fields before download
    if (!validateMandatoryFields()) {
      return;
    }
    
    setGenerating(true);
    
    try {
      // Generate the same HTML content used for HTML download
      let htmlContent = await generateCompleteHTMLContent();
      
      // Remove ONLY the live Windy iframe (which doesn't render in PDF), keep the captured snapshots
      // The structure is: <!-- Live Windy Map Embed --> <div> <p> <div> <iframe></iframe> </div> </div>
      htmlContent = htmlContent.replace(
        /<!-- Live Windy Map Embed -->[\s\S]*?<\/iframe>\s*<\/div>\s*<\/div>/g,
        ''
      );
      
      // Send to backend for PDF conversion using Playwright
      const response = await fetch(`${API}/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html_content: htmlContent,
          filename: getDownloadFilename('pdf')
        })
      });
      
      if (!response.ok) {
        throw new Error('PDF generation failed');
      }
      
      const data = await response.json();
      
      if (data.success && data.pdf_base64) {
        // Convert base64 to blob and download
        const binaryString = atob(data.pdf_base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || getDownloadFilename('pdf');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  // Generate complete HTML content (shared between download and email)
  const generateCompleteHTMLContent = async () => {
    // Generate route weather HTML from weatherPredictionData (works for ALL FTX designators)
    let routeWeatherHtml = '';
    let staticMapImage = null;
    
    if (weatherPredictionData && !notStandardRouting) {
      // Get selected routes for the map (preserve as separate routes for multi-colored rendering)
      let selectedRoutes = [];
      let selectedRouteNames = [];
      
      // If we have multiple routes and selection info, filter to selected routes only
      if (weatherPredictionData.routes && weatherPredictionData.routes.length > 0 && weatherPredictionData.selectedRouteIndices) {
        const selectedIndices = weatherPredictionData.selectedRouteIndices;
        
        weatherPredictionData.routes.forEach((route, index) => {
          if (selectedIndices.includes(index)) {
            selectedRoutes.push({
              name: route.name || `Route ${index + 1}`,
              coordinates: route.coordinates || []
            });
            selectedRouteNames.push(route.name?.replace('.kml', '').replace('.kmz', '') || `Route ${index + 1}`);
          }
        });
      } else if (weatherPredictionData.routeCoordinates && weatherPredictionData.routeCoordinates.length > 0) {
        // Fallback to single route
        selectedRoutes.push({
          name: weatherPredictionData.routeName || 'Route',
          coordinates: weatherPredictionData.routeCoordinates
        });
      }
      
      // Try to fetch static map if we have routes
      if (selectedRoutes.length > 0) {
        try {
          // Prepare waypoints data for the map
          const waypointsForMap = (weatherPredictionData.routeWaypointWeather?.waypoints || []).map((wp, idx) => ({
            lat: wp.lat,
            lon: wp.lon,
            index: idx + 1,
            category: wp.weather_summary?.category || 'VFR',
            location_name: wp.location_name || `Wx #${idx + 1}`
          }));
          
          const mapResponse = await fetch(`${API}/static-map/route`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              routes: selectedRoutes,  // Pass routes separately for multi-colored rendering
              airports: (weatherPredictionData.filteredWeatherData || weatherPredictionData.weatherData || []).map(a => ({
                icao: a.icao,
                lat: a.lat,
                lon: a.lon,
                flight_category: a.metar?.flight_category || 'UNKNOWN'
              })),
              waypoints: waypointsForMap,  // Add weather waypoints to map
              width: 600,
              height: 300
            })
          });
          
          if (mapResponse.ok) {
            const mapData = await mapResponse.json();
            staticMapImage = mapData.image;
          }
        } catch (mapErr) {
          console.error("Failed to fetch static map:", mapErr);
        }
      }
      
      const routeWeatherResult = generateRouteWeatherHtml(staticMapImage, true, selectedRouteNames, selectedRoutes);
      routeWeatherHtml = routeWeatherResult.html || '';
      var mapScript = routeWeatherResult.script || '';
    }
    
    // Fetch radar image
    let radarImageData = radarImageBase64 || null;

    // SVG Icons as inline data
    const planeIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>`;
    
    // Generate full HTML document
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Weather Briefing - ${icao || 'NSAS'} - ${getCurrentDate()}</title>
  <!-- Leaflet CSS for interactive map -->
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #1e293b; background: #fff; }
    .container { max-width: 280mm; margin: 0 auto; padding: 15px; }
    
    /* Header */
    .header { display: flex; align-items: center; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #4f46e5; }
    .logo { height: 50px; width: auto; margin-right: 15px; object-fit: contain; }
    .header-text h1 { font-size: 20px; color: #1e293b; margin-bottom: 2px; }
    .header-text p { font-size: 12px; color: #64748b; }
    .header-icon { margin-left: auto; color: #4f46e5; }
    
    /* Sections */
    .section { margin-bottom: 18px; }
    .section-title { font-size: 13px; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
    .route-title { color: #4f46e5; border-color: #c7d2fe; }
    
    /* Tables */
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    td { padding: 6px 8px; border: 1px solid #e2e8f0; }
    .label-cell { background: #f8fafc; font-weight: bold; width: 25%; }
    
    /* Flight Categories */
    .flight-cat { font-weight: bold; margin-left: 5px; }
    .flight-cat.vfr { color: #047857; }
    .flight-cat.mvfr { color: #1d4ed8; }
    .flight-cat.ifr { color: #dc2626; }
    .flight-cat.lifr { color: #7c3aed; }
    
    /* Airport Cards */
    .airport-card { background: #f8fafc; padding: 10px; border: 1px solid #e2e8f0; border-radius: 3px; margin-bottom: 8px; }
    .airport-header { font-size: 12px; margin-bottom: 6px; padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; }
    .wx-data { margin-bottom: 4px; }
    .wx-label { display: block; font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: bold; margin-bottom: 2px; }
    .wx-data code { font-family: 'Courier New', monospace; font-size: 11px; color: #1e293b; word-break: break-all; }
    
    /* Route Info */
    .route-info { background: #eef2ff; padding: 10px; border: 1px solid #c7d2fe; border-radius: 3px; margin-bottom: 10px; }
    .route-path { color: #4f46e5; margin-bottom: 5px; }
    
    /* Map */
    .map-container { text-align: center; margin-bottom: 10px; }
    .route-map { width: 100%; height: auto; border-radius: 4px; border: 1px solid #e2e8f0; }
    .map-caption { font-size: 10px; color: #64748b; margin-top: 5px; }
    
    /* Prediction Box */
    .prediction-box { padding: 10px; border-radius: 3px; margin-bottom: 10px; }
    .prediction-box.low { background: #f0fdf4; border: 1px solid #bbf7d0; }
    .prediction-box.moderate { background: #fffbeb; border: 1px solid #fde68a; }
    .prediction-box.high { background: #fef2f2; border: 1px solid #fecaca; }
    .prediction-header { font-size: 13px; margin-bottom: 10px; }
    .risk-low { color: #047857; font-weight: bold; }
    .risk-moderate { color: #b45309; font-weight: bold; }
    .risk-high { color: #dc2626; font-weight: bold; }
    .prediction-summary { font-size: 13px; color: #374151; margin-bottom: 10px; }
    .prediction-conditions { font-size: 12px; color: #6b7280; margin-bottom: 10px; }
    .prediction-notes { border-top: 1px solid currentColor; padding-top: 8px; opacity: 0.3; border-color: inherit; }
    .prediction-box.low .prediction-notes { border-color: #bbf7d0; }
    .prediction-box.moderate .prediction-notes { border-color: #fde68a; }
    .prediction-box.high .prediction-notes { border-color: #fecaca; }
    .notes-header { font-size: 12px; font-weight: bold; color: #4b5563; margin-bottom: 6px; }
    .prediction-notes ul { margin: 0; padding-left: 18px; font-size: 12px; color: #374151; }
    .prediction-notes li { margin-bottom: 3px; }
    .ai-weather-briefing { margin-top: 10px; padding-top: 10px; border-top: 1px solid inherit; }
    .ai-briefing-header { font-size: 12px; font-weight: bold; color: #7c3aed; margin-bottom: 6px; }
    .ai-briefing-content { font-size: 12px; color: #374151; white-space: pre-wrap; line-height: 1.6; }
    
    /* Weather Analysis by AI Section */
    .weather-analysis-section { margin: 15px 0; padding: 14px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; }
    .weather-analysis-section.moderate { background: #fffbeb; border-color: #fde68a; }
    .weather-analysis-section.high { background: #fef2f2; border-color: #fecaca; }
    .analysis-title { font-size: 15px; font-weight: bold; color: #1e293b; margin-bottom: 12px; }
    .analysis-title .time-window { font-size: 13px; color: #64748b; font-weight: normal; margin-left: 8px; }
    .analysis-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
    .analysis-card { background: white; border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px; }
    .card-title { font-size: 13px; font-weight: bold; color: #374151; text-transform: uppercase; margin-bottom: 8px; }
    .card-title .card-subtitle { font-weight: normal; color: #9ca3af; }
    .taf-airport { border-bottom: 1px solid #f1f5f9; padding-bottom: 6px; margin-bottom: 6px; }
    .taf-airport:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
    .taf-header { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
    .taf-icao { font-weight: bold; font-size: 13px; }
    .taf-name { font-size: 12px; color: #64748b; }
    .taf-cat { font-size: 12px; font-weight: bold; padding: 2px 6px; border-radius: 2px; margin-left: auto; }
    .taf-cat.cat-vfr { background: #d1fae5; color: #047857; }
    .taf-cat.cat-mvfr { background: #fef3c7; color: #b45309; }
    .taf-cat.cat-ifr { background: #fee2e2; color: #dc2626; }
    .taf-cat.cat-lifr { background: #ede9fe; color: #7c3aed; }
    .taf-details { font-size: 12px; color: #4b5563; display: flex; gap: 8px; }
    .taf-details .warn { color: #dc2626; font-weight: bold; }
    .taf-issues { font-size: 11px; color: #dc2626; margin-top: 2px; }
    .taf-no-coverage { font-size: 12px; color: #d97706; background: #fef3c7; padding: 4px 8px; border-radius: 3px; }
    .no-data { font-size: 12px; color: #9ca3af; font-style: italic; }
    .ifr-warning { font-size: 13px; color: #dc2626; font-weight: bold; margin-bottom: 6px; }
    .limitations-list { font-size: 12px; }
    .limit-item { margin-bottom: 2px; color: #6b7280; }
    .limit-item.checked { color: #dc2626; }
    .outcome-card.go { background: #d1fae5; border-color: #86efac; }
    .outcome-card.caution { background: #fef3c7; border-color: #fde68a; }
    .outcome-card.nogo { background: #fee2e2; border-color: #fecaca; }
    .outcome-result { font-size: 14px; font-weight: bold; margin-bottom: 4px; }
    .outcome-card.go .outcome-result { color: #047857; }
    .outcome-card.caution .outcome-result { color: #b45309; }
    .outcome-card.nogo .outcome-result { color: #dc2626; }
    .outcome-reasons { font-size: 12px; color: #374151; }
    .outcome-reasons div { margin-bottom: 2px; }
    .analysis-summary { padding: 10px; border-radius: 4px; margin-top: 8px; }
    .analysis-summary.low { background: #ecfdf5; border: 1px solid #a7f3d0; }
    .analysis-summary.moderate { background: #fffbeb; border: 1px solid #fde68a; }
    .analysis-summary.high { background: #fef2f2; border: 1px solid #fecaca; }
    .analysis-summary h4 { font-size: 13px; font-weight: bold; margin-bottom: 4px; }
    .analysis-summary p { font-size: 12px; color: #374151; line-height: 1.5; }
    
    /* Windy Maps Section */
    .windy-maps-section { margin: 15px 0; padding: 14px; background: #ecfeff; border: 1px solid #a5f3fc; border-radius: 6px; }
    .windy-title { font-size: 15px; font-weight: bold; color: #0891b2; margin-bottom: 12px; }
    .windy-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .windy-map-item { background: white; border: 1px solid #cffafe; border-radius: 4px; overflow: hidden; }
    .windy-map-header { background: #cffafe; padding: 6px 10px; font-size: 13px; font-weight: bold; color: #0e7490; display: flex; justify-content: space-between; align-items: center; }
    .windy-link { font-size: 12px; color: #0891b2; text-decoration: none; font-weight: normal; }
    .windy-link:hover { text-decoration: underline; }
    .windy-gif { width: 100%; height: auto; display: block; }
    .windy-caption { font-size: 12px; color: #64748b; margin-bottom: 10px; }
    .windy-links-section { margin-top: 12px; padding-top: 12px; border-top: 1px solid #a5f3fc; }
    .windy-all-links { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px; }
    .windy-layer-link { font-size: 12px; color: #0891b2; text-decoration: none; background: white; padding: 6px 10px; border-radius: 4px; border: 1px solid #a5f3fc; }
    .windy-layer-link:hover { background: #cffafe; text-decoration: none; }
    /* Windy Snapshot styles */
    .windy-snapshot-container { background: white; border: 1px solid #cffafe; border-radius: 4px; overflow: hidden; }
    .windy-snapshot-header { background: #cffafe; padding: 8px 12px; font-size: 14px; font-weight: bold; color: #0e7490; }
    .windy-snapshot-img { width: 100%; height: auto; display: block; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; }
    .windy-snapshot-img:hover { transform: scale(1.02); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); }
    .click-hint { font-size: 11px; color: #64748b; text-align: center; padding: 6px; background: #f0f9ff; }
    
    /* Lightbox styles for full window preview */
    .lightbox-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.95);
      z-index: 9999;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
    .lightbox-overlay.active { display: flex; }
    .lightbox-content {
      max-width: 95%;
      max-height: 95%;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .lightbox-img {
      max-width: 100%;
      max-height: 85vh;
      border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      object-fit: contain;
    }
    .lightbox-caption {
      color: white;
      text-align: center;
      padding: 15px;
      font-size: 16px;
      font-weight: 500;
    }
    .lightbox-close {
      position: absolute;
      top: -50px;
      right: 0;
      color: white;
      font-size: 36px;
      cursor: pointer;
      background: none;
      border: none;
      padding: 5px 15px;
      transition: color 0.2s;
    }
    .lightbox-close:hover { color: #fbbf24; }
    .lightbox-nav {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      color: white;
      font-size: 48px;
      cursor: pointer;
      background: rgba(0, 0, 0, 0.4);
      border: none;
      padding: 25px 20px;
      border-radius: 8px;
      transition: background 0.2s;
    }
    .lightbox-nav:hover { background: rgba(0, 0, 0, 0.7); }
    .lightbox-prev { left: 20px; }
    .lightbox-next { right: 20px; }
    
    /* Checkboxes */
    .checkbox { font-family: monospace; }
    .checkbox.checked { color: #f59e0b; }
    .checkbox.unchecked { color: #94a3b8; }
    .checkbox.danger { color: #ef4444; }
    .checkbox.info { color: #3b82f6; }
    
    /* Radar */
    .radar-container { text-align: center; margin: 10px 0; }
    .radar-img { max-width: 100%; height: auto; border-radius: 4px; border: 1px solid #e2e8f0; }
    .radar-caption { font-size: 10px; color: #64748b; margin-top: 5px; }
    
    /* Notes */
    .notes-box { background: #fffbeb; padding: 10px; border: 1px solid #fde68a; border-radius: 3px; }
    .notes-box p { font-size: 11px; color: #1e293b; white-space: pre-wrap; }
    
    /* ===================== PILOT DECISION SECTIONS ===================== */
    /* These are the critical decision-maker sections with enhanced styling */
    
    /* WX Analysis by Crew - Main pilot opinion section */
    .pilot-section { 
      margin-bottom: 20px; 
      border: 2px solid #1e40af; 
      border-radius: 6px; 
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(30, 64, 175, 0.15);
    }
    .pilot-section-header {
      background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
      color: white;
      padding: 10px 14px;
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 1px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .pilot-section-header::before {
      content: '✈';
      font-size: 16px;
    }
    .pilot-section-content {
      padding: 14px;
      background: #f8fafc;
    }
    .pilot-section .notes-box {
      background: #fef3c7;
      border: 2px solid #f59e0b;
      border-radius: 4px;
      padding: 14px;
    }
    .pilot-section .notes-box p {
      font-size: 14px;
      line-height: 1.6;
      color: #1e293b;
      font-weight: 500;
    }
    
    /* Flight Limitations - Enhanced checkbox styling */
    .limitations-section {
      margin-bottom: 20px;
      border: 2px solid #dc2626;
      border-radius: 6px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(220, 38, 38, 0.15);
    }
    .limitations-header {
      background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
      color: white;
      padding: 10px 14px;
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 1px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .limitations-header::before {
      content: '⚠';
      font-size: 16px;
    }
    .limitations-content {
      padding: 14px;
      background: #fef2f2;
    }
    .limitations-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .limitation-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 14px;
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s;
    }
    .limitation-item.active {
      background: #fef3c7;
      border-color: #f59e0b;
      color: #92400e;
    }
    .limitation-item.danger {
      background: #fee2e2;
      border-color: #ef4444;
      color: #991b1b;
    }
    .limitation-checkbox {
      font-family: monospace;
      font-size: 18px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      background: #f1f5f9;
      color: #94a3b8;
    }
    .limitation-item.active .limitation-checkbox,
    .limitation-item.danger .limitation-checkbox {
      background: #f59e0b;
      color: white;
    }
    .limitation-item.danger .limitation-checkbox {
      background: #ef4444;
    }
    
    /* Crew Recommendations - Enhanced emphasis */
    .recommendations-section {
      margin-bottom: 20px;
      border: 2px solid #059669;
      border-radius: 6px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(5, 150, 105, 0.15);
    }
    .recommendations-header {
      background: linear-gradient(135deg, #059669 0%, #10b981 100%);
      color: white;
      padding: 10px 14px;
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 1px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .recommendations-header::before {
      content: '📋';
      font-size: 16px;
    }
    .recommendations-content {
      padding: 14px;
      background: #f0fdf4;
    }
    .recommendations-text {
      font-size: 14px;
      line-height: 1.6;
      color: #1e293b;
      font-weight: 500;
      white-space: pre-wrap;
      background: white;
      padding: 14px;
      border-radius: 4px;
      border: 1px solid #86efac;
    }
    
    /* Decision Criteria Reference Section */
    .criteria-section {
      margin-bottom: 20px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      overflow: hidden;
      background: #f8fafc;
      page-break-inside: avoid;
    }
    .criteria-header {
      background: linear-gradient(135deg, #0891b2 0%, #22d3ee 100%);
      color: white;
      padding: 8px 14px;
      font-size: 12px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .criteria-content {
      padding: 12px;
    }
    .criteria-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 10px;
    }
    .criteria-card {
      padding: 8px;
      border-radius: 4px;
      font-size: 10px;
    }
    .criteria-card.nogo {
      background: #fef2f2;
      border: 1px solid #fecaca;
    }
    .criteria-card.caution {
      background: #fffbeb;
      border: 1px solid #fde68a;
    }
    .criteria-card.go {
      background: #ecfdf5;
      border: 1px solid #a7f3d0;
    }
    .criteria-card-title {
      font-weight: bold;
      margin-bottom: 4px;
      font-size: 11px;
    }
    .criteria-card.nogo .criteria-card-title { color: #dc2626; }
    .criteria-card.caution .criteria-card-title { color: #b45309; }
    .criteria-card.go .criteria-card-title { color: #047857; }
    .criteria-list {
      margin: 0;
      padding-left: 14px;
      color: #374151;
    }
    .criteria-list li {
      margin-bottom: 2px;
    }
    .criteria-params {
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid #e2e8f0;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      font-size: 10px;
    }
    .criteria-param {
      background: white;
      padding: 4px 8px;
      border-radius: 3px;
      border: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
    }
    .criteria-param-label { color: #64748b; }
    .criteria-param-value { font-weight: bold; font-family: monospace; }
    
    /* Footer */
    .footer { text-align: center; padding-top: 15px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 11px; }
    .footer p { margin: 3px 0; }
    
    /* Print styles */
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .container { padding: 10mm; }
    }
  </style>
</head>
<body>
  <div class="container">
    <!-- Header -->
    <div class="header">
      ${logoBase64 ? `<img src="${logoBase64}" alt="NSAS" class="logo" />` : '<div class="logo"></div>'}
      <div class="header-text">
        <h1>Weather Briefing</h1>
        <p>${icao ? `${icao} • ` : ''}${getCurrentDate()}</p>
      </div>
      <div class="header-icon">${planeIcon}</div>
    </div>

    <!-- Flight Information -->
    <div class="section">
      <h2 class="section-title">Flight Information</h2>
      <table>
        <tr>
          <td class="label-cell">FTX Designator</td>
          <td>${getFtxDesignatorName(formData.ftxDesignator)}</td>
          <td class="label-cell">Registration</td>
          <td>${formData.registration || "-"}</td>
        </tr>
        <tr>
          <td class="label-cell">Aircraft Type</td>
          <td>${getAircraftTypeName(formData.aircraftType)}</td>
          <td class="label-cell">Callsign</td>
          <td>${formData.callsign || "-"}</td>
        </tr>
      </table>
    </div>

    ${routeWeatherHtml}

    <!-- AI Weather Analysis Section -->
    ${effectiveExplanation ? `
      <div class="section">
        <h2 class="section-title" style="color: #7c3aed; border-color: #c4b5fd;">AI Weather Analysis</h2>
        <div style="background: #f5f3ff; padding: 14px; border: 1px solid #c4b5fd; border-radius: 6px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
            <span style="font-size: 16px;">✨</span>
            <span style="font-size: 12px; font-weight: bold; color: #7c3aed;">Plain Language Weather Briefing</span>
          </div>
          <div style="font-size: 12px; color: #374151; white-space: pre-wrap; line-height: 1.6;">${effectiveExplanation}</div>
        </div>
      </div>
    ` : ''}

    <!-- Windy Weather Maps Section -->
    ${(() => {
      // Calculate route center for Windy embed
      let windyLat = 51.2; // Default to EBOS area
      let windyLon = 2.86;
      if (weatherPredictionData?.weatherData?.length > 0) {
        const airports = weatherPredictionData.weatherData;
        windyLat = airports.reduce((sum, a) => sum + (a.lat || 0), 0) / airports.length;
        windyLon = airports.reduce((sum, a) => sum + (a.lon || 0), 0) / airports.length;
      }
      
      const hasSnapshots = windySnapshots && windySnapshots.length > 0;
      
      return `
      <div class="windy-maps-section">
        <div class="windy-title">Weather Maps (Windy.com)</div>
        
        <!-- Live Windy Map Embed -->
        <div style="margin-bottom: 15px;">
          <p style="font-size: 12px; color: #64748b; margin-bottom: 8px;">Live Weather Radar (click to interact):</p>
          <div style="width: 100%; border-radius: 6px; overflow: hidden; border: 1px solid #a5f3fc;">
            <iframe 
              src="https://embed.windy.com/embed2.html?lat=${windyLat}&lon=${windyLon}&detailLat=${windyLat}&detailLon=${windyLon}&width=650&height=400&zoom=7&level=surface&overlay=radar&product=ecmwf&menu=&message=true&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1"
              style="width: 100%; height: 400px; border: none;"
              frameborder="0"
              allowfullscreen>
            </iframe>
          </div>
        </div>
        
        ${hasSnapshots ? `
        <!-- Captured Snapshots -->
        <p class="windy-caption">Captured weather layer snapshots (click to enlarge):</p>
        <div class="windy-grid">
          ${windySnapshots.map((snapshot, idx) => `
            <div class="windy-snapshot-container">
              <div class="windy-snapshot-header">
                <span>${snapshot.layerIcon || '📸'} ${snapshot.layerName || snapshot.layer || 'Weather Layer'}</span>
              </div>
              ${snapshot.data ? `<img src="${snapshot.data}" alt="${snapshot.layerName || snapshot.layer || 'Weather'}" class="windy-snapshot-img" onclick="openLightbox(${idx})" data-index="${idx}" style="width: 100%;" />` : ''}
            </div>
          `).join('')}
        </div>
        ` : ''}
        
        <div class="windy-links-section">
          <p style="font-size: 12px; color: #64748b; margin-bottom: 8px;">Interactive weather layers (click to open in Windy.com):</p>
          <div class="windy-all-links">
            <a href="https://www.windy.com/?${windyLat},${windyLon},8,i:radar" target="_blank" class="windy-layer-link">📡 Weather Radar</a>
            <a href="https://www.windy.com/?${windyLat},${windyLon},8,i:wind" target="_blank" class="windy-layer-link">💨 Wind</a>
            <a href="https://www.windy.com/?${windyLat},${windyLon},8,i:visibility" target="_blank" class="windy-layer-link">👁️ Visibility</a>
            <a href="https://www.windy.com/?${windyLat},${windyLon},8,i:lclouds" target="_blank" class="windy-layer-link">☁️ Low Clouds</a>
            <a href="https://www.windy.com/?${windyLat},${windyLon},8,i:rain" target="_blank" class="windy-layer-link">🌧️ Rain</a>
            <a href="https://www.windy.com/?${windyLat},${windyLon},8,i:waves" target="_blank" class="windy-layer-link">🌊 Waves</a>
          </div>
        </div>
      </div>
    `;
    })()}

    <!-- WX Analysis by Crew - PILOT DECISION SECTION -->
    <div class="pilot-section">
      <div class="pilot-section-header">WX Analysis by Crew${notStandardRouting ? ' (Non-Standard Routing)' : ''}</div>
      <div class="pilot-section-content">
        ${isMultiAirport && multiAirportData?.length > 0 ? 
          multiAirportData.map(airport => `
            <div class="airport-card">
              <div class="airport-header">
                <strong>${airport.icao}</strong>
                ${airport.metar?.flight_category ? `<span class="flight-cat ${airport.metar.flight_category.toLowerCase()}">(${airport.metar.flight_category})</span>` : ''}
                - <span style="color: #64748b;">${getAirportName(airport.icao)}</span>
              </div>
              ${airport.metar?.raw ? `<div class="wx-data"><span class="wx-label">METAR</span><code>${airport.metar.raw}</code></div>` : ''}
              ${airport.taf?.raw ? `<div class="wx-data"><span class="wx-label">TAF</span><code>${airport.taf.raw}</code></div>` : ''}
            </div>
          `).join('')
        : `
          ${metar ? `
            <div class="airport-card">
              <div class="wx-data"><span class="wx-label">Current METAR</span><code>${effectiveMetar}</code></div>
            </div>
          ` : ''}
          ${taf ? `
            <div class="airport-card">
              <div class="wx-data"><span class="wx-label">Terminal Forecast (TAF)</span><code>${effectiveTaf}</code></div>
            </div>
          ` : ''}
          ${formData.missionWeather ? `
            <div class="notes-box">
              <p>${formData.missionWeather}</p>
            </div>
          ` : ''}
        `}
      </div>
    </div>

    <!-- Flight Limitations - PILOT DECISION SECTION -->
    <div class="limitations-section">
      <div class="limitations-header">Flight Limitations</div>
      <div class="limitations-content">
        <div class="limitations-grid">
          <div class="limitation-item ${formData.poorVisibility ? 'active' : ''}">
            <span class="limitation-checkbox">${formData.poorVisibility ? '✓' : ''}</span>
            <span>Poor visibility</span>
          </div>
          <div class="limitation-item ${formData.lowClouds ? 'active' : ''}">
            <span class="limitation-checkbox">${formData.lowClouds ? '✓' : ''}</span>
            <span>Low clouds</span>
          </div>
          <div class="limitation-item ${formData.crosswind ? 'active' : ''}">
            <span class="limitation-checkbox">${formData.crosswind ? '✓' : ''}</span>
            <span>Crosswind &gt; 20 KTS</span>
          </div>
          <div class="limitation-item ${formData.thunderstorm ? 'danger' : ''}">
            <span class="limitation-checkbox">${formData.thunderstorm ? '✓' : ''}</span>
            <span>Thunderstorm / CB</span>
          </div>
          <div class="limitation-item ${formData.icingConditions ? 'active' : ''}" style="grid-column: span 2;">
            <span class="limitation-checkbox">${formData.icingConditions ? '✓' : ''}</span>
            <span>Known icing conditions</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Crew Recommendations - PILOT DECISION SECTION -->
    ${formData.crewRecommendations ? `
      <div class="recommendations-section">
        <div class="recommendations-header">Crew Recommendations</div>
        <div class="recommendations-content">
          <div class="recommendations-text">${formData.crewRecommendations}</div>
        </div>
      </div>
    ` : ''}

    <!-- Additional Notes -->
    ${formData.additionalNotes ? `
      <div class="section">
        <h2 class="section-title">Additional Notes</h2>
        <div class="notes-box">
          <p>${formData.additionalNotes}</p>
        </div>
      </div>
    ` : ''}

    <!-- NOTAMs Section for HTML Report -->
    ${includeNotams && Object.keys(getFilteredNotams()).length > 0 ? `
    <div class="section">
      <h2 class="section-title" style="color: #475569; border-color: #e2e8f0;">NOTAMs (${Object.values(getFilteredNotams()).flat().length} notices)</h2>
      ${Object.entries(getFilteredNotams()).map(([airportIcao, airportNotams]) => {
        const explanation = getFilteredExplanations()[airportIcao];
        return `
        <div style="margin-bottom: 20px;">
          <div style="font-size: 12px; color: #334155; font-weight: 600; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #f1f5f9;">
            ${airportIcao} <span style="font-weight: 400; color: #94a3b8;">(${airportNotams.length})</span>
          </div>
          ${explanation?.items?.length > 0 ? 
            explanation.items.map((item) => `
              <div style="padding: 10px 14px; margin-bottom: 8px; background: #f8fafc; border-radius: 6px; font-size: 11px; color: #334155; line-height: 1.6; display: flex; gap: 16px;">
                <div style="flex-shrink: 0; width: 160px; display: flex; align-items: flex-start; gap: 8px;">
                  <span>${item.category}</span>
                  <span style="padding: 2px 6px; background: #ffffff; border-radius: 3px; font-family: monospace; color: #64748b; font-size: 10px;">${item.id}</span>
                </div>
                <div style="flex: 1;">${item.plain}</div>
              </div>
            `).join('')
          :
            airportNotams.map((notam) => `
              <div style="padding: 10px 14px; margin-bottom: 8px; background: #f8fafc; border-radius: 6px; font-family: monospace; font-size: 10px; color: #475569; line-height: 1.5;">
                ${notam}
              </div>
            `).join('')
          }
        </div>
      `}).join('')}
    </div>
    ` : ''}

    <!-- Crew -->
    <div class="section">
      <h2 class="section-title">Crew</h2>
      <table>
        <tr>
          <td class="label-cell">Pilot in Command (PIC)</td>
          <td>${formData.crewPIC || "-"}</td>
          <td class="label-cell">PM</td>
          <td>${formData.crewPM || "-"}</td>
        </tr>
        <tr>
          <td class="label-cell">Sensor Operator (SO)</td>
          <td>${formData.crewSensorOperator || "-"}</td>
          <td class="label-cell">Liaison Officer</td>
          <td>${(formData.crewLiaisonOfficer || "-").replace(/\s*🥳\s*/g, '')}</td>
        </tr>
        ${formData.missionWindowTiming ? `
        <tr>
          <td class="label-cell">Mission Window</td>
          <td colspan="3">${formData.missionWindowTiming}</td>
        </tr>
        ` : ''}
      </table>
    </div>

    <!-- Footer -->
    <div class="footer">
      <p>All times in Zulu (UTC)</p>
      <p>Created on ${new Date(briefingCreatedAt).toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' })} UTC</p>
    </div>
  </div>

  <!-- Lightbox Overlay for Snapshot Enlargement -->
  <div id="lightbox-overlay" class="lightbox-overlay" onclick="closeLightbox(event)">
    <div class="lightbox-content" onclick="event.stopPropagation()">
      <button class="lightbox-close" onclick="closeLightbox()">&times;</button>
      <button class="lightbox-nav lightbox-prev" onclick="navigateLightbox(-1)">&#8249;</button>
      <img id="lightbox-img" class="lightbox-img" src="" alt="Weather snapshot" />
      <button class="lightbox-nav lightbox-next" onclick="navigateLightbox(1)">&#8250;</button>
      <div id="lightbox-caption" class="lightbox-caption"></div>
    </div>
  </div>

  <script>
    // Lightbox functionality for enlarging Windy snapshots
    const snapshotData = ${JSON.stringify(windySnapshots.map(s => ({ src: s.data, label: s.layerName || s.layer || 'Weather Layer', icon: s.layerIcon || '📸' })))};
    let currentIndex = 0;

    function openLightbox(index) {
      if (!snapshotData || snapshotData.length === 0) return;
      currentIndex = index;
      updateLightboxContent();
      document.getElementById('lightbox-overlay').classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox(event) {
      if (event && event.target !== event.currentTarget) return;
      document.getElementById('lightbox-overlay').classList.remove('active');
      document.body.style.overflow = '';
    }

    function navigateLightbox(direction) {
      if (!snapshotData || snapshotData.length === 0) return;
      currentIndex = (currentIndex + direction + snapshotData.length) % snapshotData.length;
      updateLightboxContent();
    }

    function updateLightboxContent() {
      const snapshot = snapshotData[currentIndex];
      if (!snapshot) return;
      document.getElementById('lightbox-img').src = snapshot.src;
      document.getElementById('lightbox-caption').textContent = snapshot.icon + ' ' + snapshot.label + ' (' + (currentIndex + 1) + '/' + snapshotData.length + ')';
    }

    // Keyboard navigation
    document.addEventListener('keydown', function(e) {
      const overlay = document.getElementById('lightbox-overlay');
      if (!overlay.classList.contains('active')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    });
  </script>
  ${mapScript || ''}
</body>
</html>`;

    return htmlContent;
  };

  // Handle download HTML report
  const handleDownloadHTML = async () => {
    // Validate mandatory fields before download
    if (!validateMandatoryFields()) {
      return;
    }
    
    setGenerating(true);
    
    const htmlContent = await generateCompleteHTMLContent();
    
    // Create and download the HTML file
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getDownloadFilename('html');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setGenerating(false);
  };

  // Handle Send LO - send briefing to Liaison Officer via email with HTML attachment
  const handleSendLO = async () => {
    // Only available for FTX designators with weather prediction data
    if (!weatherPredictionData) {
      toast.error("Send LO requires a configured FTX route with weather analysis");
      return;
    }
    
    // Get the LO email from the FTX config
    const selectedFtx = FTX_DESIGNATORS.find(ftx => 
      (typeof ftx === 'string' ? ftx : ftx.name) === formData.ftxDesignator
    );
    const loEmail = typeof selectedFtx === 'object' ? selectedFtx.loEmail : null;
    
    if (!loEmail) {
      toast.error(`No Liaison Officer email configured for ${formData.ftxDesignator}. Please configure it in the Admin Panel.`);
      return;
    }
    
    // Validate mandatory fields before sending
    if (!validateMandatoryFields()) {
      return;
    }

    setGenerating(true);
    toast.info("Generating complete HTML report...");

    try {
      // Generate the complete HTML content (same as download)
      const htmlContent = await generateCompleteHTMLContent();
      
      // Verify content is complete
      if (!htmlContent || htmlContent.length < 1000) {
        throw new Error("HTML content generation incomplete");
      }
      
      toast.info("HTML report generated, preparing email...");
      
      // Small delay to ensure everything is processed
      await new Promise(resolve => setTimeout(resolve, 500));

      // Prepare email data
      const htmlFilename = getDownloadFilename('html');
      const subject = `Weather Briefing - ${formData.ftxDesignator} - ${getCurrentDate()}`;
      
      // Build crew recommendations section for email
      const crewRecommendationsSection = formData.crewRecommendations 
        ? `\nCrew Recommendations:\n${formData.crewRecommendations}\n` 
        : '';
      
      const body = `Dear Liaison Officer,

Please find attached the weather briefing for ${formData.ftxDesignator}.
${crewRecommendationsSection}
Flight Details:
- Date: ${getCurrentDate()}
- Route: EBOS → LFAC → LFAT
- PIC: ${formData.crewPIC || 'N/A'}
- Aircraft: ${formData.registration || 'N/A'}

Attachment:
- ${htmlFilename} (HTML Report)

Best regards,
NSAS TOOLBOX`;

      // Send email via backend with HTML attachment
      toast.info(`Sending email to ${loEmail}...`);
      const response = await axios.post(`${API}/send-briefing-email`, {
        to_email: loEmail,
        subject: subject,
        body: body,
        html_content: htmlContent,
        html_filename: htmlFilename
      });

      if (response.data.success) {
        toast.success(`HTML Briefing sent to Liaison Officer (${loEmail})`);
        
        // Auto-save to library after successful email send
        try {
          const weatherDataSnapshot = {
            metar: metar,
            taf: taf,
            explanation: explanation,
            multiAirportWeather: isMultiAirport ? multiAirportData : [],
            routeMapImage: routeMapImage,
            radarImageBase64: radarImageBase64,
            windyMapsGifs: windyMapsData.visible && Object.keys(windyMapsData.gifs).length > 0 
              ? windyMapsData.gifs 
              : null,
            windySnapshots: windySnapshots,
            weatherPrediction: weatherPredictionData,
            savedAt: new Date().toISOString(),
            flightCategory: flightCategory
          };

          const autoTitle = `${formData.ftxDesignator} - ${getCurrentDate()} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} (Sent to LO)`;
          
          const briefingData = {
            title: autoTitle,
            icao: icao || "EBOS",
            airports: isMultiAirport ? multiAirportData : [],
            formData: formData,
            flightCategory: flightCategory,
            createdAt: briefingCreatedAt,
            savedWeatherData: weatherDataSnapshot
          };

          await axios.post(`${API}/briefings`, briefingData);
          toast.success("Briefing also saved to library");
        } catch (saveError) {
          console.error("Failed to auto-save briefing:", saveError);
          // Don't show error toast - email was sent successfully
        }
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to send email");
    } finally {
      setGenerating(false);
    }
  };

  // Validate mandatory fields for save/download
  const validateMandatoryFields = () => {
    const missingFields = [];
    
    if (!formData.registration) missingFields.push("Registration");
    if (!formData.aircraftType) missingFields.push("Aircraft Type");
    if (!formData.callsign) missingFields.push("Callsign");
    if (!formData.crewPIC) missingFields.push("Pilot in Command (PIC)");
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in mandatory fields: ${missingFields.join(", ")}`);
      return false;
    }
    return true;
  };

  // Handle save briefing
  const handleSaveBriefing = async () => {
    if (!briefingTitle.trim()) {
      toast.error("Please enter a title for the briefing");
      return;
    }
    
    // Validate mandatory fields
    if (!validateMandatoryFields()) {
      return;
    }

    setSaving(true);
    try {
      // Create a snapshot of all current weather data
      const weatherDataSnapshot = {
        // Single airport weather
        metar: metar,
        taf: taf,
        explanation: explanation,
        // Multi-airport weather (already includes METAR/TAF per airport)
        multiAirportWeather: isMultiAirport ? multiAirportData : [],
        // Route map image for SPARROW 3
        routeMapImage: routeMapImage,
        // Radar image
        radarImageBase64: radarImageBase64,
        // Windy Maps GIFs (only if generated by user)
        windyMapsGifs: windyMapsData.visible && Object.keys(windyMapsData.gifs).length > 0 
          ? windyMapsData.gifs 
          : null,
        // Windy layer snapshots (captured images)
        windySnapshots: windySnapshots,
        // Weather prediction data from AI analysis
        weatherPrediction: weatherPredictionData,
        // Timestamp when saved
        savedAt: new Date().toISOString(),
        // Flight category
        flightCategory: flightCategory
      };

      const briefingData = {
        title: briefingTitle.trim(),
        icao: icao || "UNKNOWN",
        airports: isMultiAirport ? multiAirportData : [],
        formData: formData,
        flightCategory: flightCategory,
        // Save the creation timestamp
        createdAt: briefingCreatedAt,
        // Save the complete weather data snapshot
        savedWeatherData: weatherDataSnapshot
      };

      if (editingBriefing) {
        // Update existing briefing - preserve original creation time and weather data
        const updateData = {
          ...briefingData,
          // Keep the original creation timestamp
          createdAt: editingBriefing.createdAt || briefingCreatedAt,
          // Keep the original saved weather data when editing
          savedWeatherData: editingBriefing.savedWeatherData || weatherDataSnapshot
        };
        await axios.put(`${API}/briefings/${editingBriefing.id}`, updateData);
        toast.success("Briefing updated successfully");
      } else {
        // Create new briefing with current weather snapshot
        await axios.post(`${API}/briefings`, briefingData);
        toast.success("Briefing saved successfully");
      }

      setShowSaveDialog(false);
      setBriefingTitle("");
      if (onSaveSuccess) onSaveSuccess();
    } catch (error) {
      console.error("Failed to save briefing:", error);
      toast.error("Failed to save briefing");
    } finally {
      setSaving(false);
    }
  };

  // Clear form
  const handleClearForm = () => {
    setFormData({
      ftxDesignator: "",
      registration: "",
      aircraftType: "",
      callsign: "",
      missionWeather: "",
      crewRecommendations: "",
      crewPIC: "",
      crewPM: "",
      crewSensorOperator: "",
      crewLiaisonOfficer: "",
      missionWindowTiming: "",
      poorVisibility: false,
      lowClouds: false,
      crosswind: false,
      thunderstorm: false,
      icingConditions: false,
    });
    setBriefingTitle("");
    if (onClearEdit) onClearEdit();
  };

  return (
    <div className="weather-card p-6" data-testid="briefing-form" ref={formRef}>
      {/* Editing Indicator */}
      {editingBriefing && (
        <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            <span className="text-sm text-indigo-700">
              Detailed view: <strong>{editingBriefing.title}</strong>
              {isLocked && <span className="ml-2 text-xs text-amber-600">(Read-only)</span>}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearForm}
            className="text-indigo-600 hover:text-indigo-800"
          >
            <X className="w-4 h-4 mr-1" />
            Close
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-100 border border-emerald-200">
            <FileText className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              NSAS TOOLBOX
            </h3>
            <p className="text-xs text-slate-500">
              {(() => {
                const visibleAirports = getAllVisibleAirports();
                if (visibleAirports.length > 0) {
                  return `${visibleAirports.join(', ')} - ${getCurrentDate()}`;
                }
                return getCurrentDate();
              })()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Only show Save/Update button when not locked, or when superuser is active */}
          {(!isLocked || isSuperuser) && (
            <Button
              variant="outline"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 hidden sm:flex"
              onClick={() => {
                const ftxName = formData.ftxDesignator || 'UNKNOWN';
                const startingAirport = formData.ftxDesignator === "SPARROW 3" 
                  ? SPARROW3_ROUTE_AIRPORTS[0]?.icao 
                  : (icao || 'UNKNOWN');
                setBriefingTitle(editingBriefing?.title || `Wx ${ftxName} - ${startingAirport} - ${getCurrentDate()}`);
                setShowSaveDialog(true);
              }}
              data-testid="save-briefing-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingBriefing ? "Update" : "Save"}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hidden sm:flex"
                disabled={generating}
                data-testid="download-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDownloadPDF} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />
                Download as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadHTML} className="cursor-pointer">
                <FileCode className="w-4 h-4 mr-2" />
                Download as HTML
              </DropdownMenuItem>
              {weatherPredictionData && (
                <DropdownMenuItem onClick={handleSendLO} className="cursor-pointer text-indigo-600">
                  <Send className="w-4 h-4 mr-2" />
                  Send LO
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBriefing ? "Update Briefing" : "Save Briefing"}</DialogTitle>
            <DialogDescription>
              {editingBriefing 
                ? "Update this briefing in your library."
                : "Save this briefing to your library for future reference."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-slate-700">Briefing Title</label>
            <Input
              value={briefingTitle}
              onChange={(e) => setBriefingTitle(e.target.value)}
              placeholder="Enter a title for this briefing..."
              className="mt-2"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveBriefing}
              disabled={saving || !briefingTitle.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingBriefing ? "Update" : "Save"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flight Information Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Plane className="w-4 h-4 text-blue-600" />
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
            Flight Information
          </h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              FTX Designator
            </Label>
            <Select 
              value={formData.ftxDesignator} 
              onValueChange={(value) => handleInputChange('ftxDesignator', value)}
              disabled={isLocked}
            >
              <SelectTrigger 
                className="bg-white border-slate-300"
                data-testid="ftx-designator-select"
              >
                <SelectValue placeholder="Select designator" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="NONE" className="font-bold">NONE</SelectItem>
                {FTX_DESIGNATORS.map((ftx, index) => {
                  const ftxName = typeof ftx === 'object' ? ftx.name : ftx;
                  return <SelectItem key={ftxName || index} value={ftxName}>{ftxName}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Registration <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.registration} 
              onValueChange={handleRegistrationChange}
              disabled={isLocked}
            >
              <SelectTrigger 
                className="bg-white border-slate-300"
                data-testid="registration-select"
              >
                <SelectValue placeholder="Select registration" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {(adminConfig?.registrations || ["OO-MSA", "OO-MSB", "OO-MSC", "OO-MSD"]).map((reg) => {
                  const regName = typeof reg === 'object' ? reg.name : reg;
                  return <SelectItem key={regName} value={regName}>{regName}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Aircraft Type <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.aircraftType} 
              onValueChange={(value) => handleInputChange('aircraftType', value)}
              disabled={isLocked}
            >
              <SelectTrigger 
                className="bg-white border-slate-300"
                data-testid="aircraft-type-select"
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {AIRCRAFT_TYPES.map((ac) => (
                  <SelectItem key={ac.value} value={ac.value}>{ac.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Callsign <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="e.g., OO-SNA"
              value={formData.callsign}
              onChange={handleCallsignChange}
              className="bg-white border-slate-300 uppercase"
              data-testid="callsign-input"
              disabled={isLocked}
            />
          </div>
      </div>
      </div>

      {/* Mission Date & Options Row */}
      <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex flex-wrap items-center gap-4">
          {/* Mission Date Picker */}
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-medium text-slate-700">Mission Date:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[160px] justify-start text-left font-normal bg-white border-slate-300 hover:bg-slate-50"
                  disabled={isLocked}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-orange-500" />
                  {formData.missionDate ? format(new Date(formData.missionDate + 'T00:00:00'), 'dd MMM yyyy') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={formData.missionDate ? new Date(formData.missionDate + 'T00:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) {
                      const dateStr = date.toISOString().split('T')[0];
                      handleInputChange('missionDate', dateStr);
                      // Re-fetch SkyLegs data for new date if FTX is selected
                      if (formData.ftxDesignator && formData.ftxDesignator !== 'NONE') {
                        fetchSkylegsFlightData(formData.ftxDesignator, dateStr);
                      }
                    }
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {/* Quick date buttons */}
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={formData.missionDate === new Date().toISOString().split('T')[0] ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  handleInputChange('missionDate', today);
                  if (formData.ftxDesignator && formData.ftxDesignator !== 'NONE') {
                    fetchSkylegsFlightData(formData.ftxDesignator, today);
                  }
                }}
                disabled={isLocked}
              >
                Today
              </Button>
              <Button
                size="sm"
                variant={formData.missionDate === new Date(Date.now() + 86400000).toISOString().split('T')[0] ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                onClick={() => {
                  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
                  handleInputChange('missionDate', tomorrow);
                  if (formData.ftxDesignator && formData.ftxDesignator !== 'NONE') {
                    fetchSkylegsFlightData(formData.ftxDesignator, tomorrow);
                  }
                }}
                disabled={isLocked}
              >
                Tomorrow
              </Button>
            </div>
          </div>

          {/* Divider */}
          {formData.ftxDesignator && formData.ftxDesignator !== 'NONE' && (
            <div className="hidden sm:block h-6 w-px bg-slate-300" />
          )}

          {/* Mission Window (from SkyLegs) */}
          {formData.missionWindowTiming && (
            <div className="flex items-center gap-2">
              <Plane className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-slate-600">Window:</span>
              <span className="text-sm font-mono font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                {formData.missionWindowTiming}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Sortie Selector & NOTAMs Row - Only show when relevant */}
      {(skylegsData?.total_sorties > 1 || formData.ftxDesignator) && (
        <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex flex-wrap items-center gap-4">
            {/* Sortie Selector - Show when multiple sorties are scheduled (multi-select) */}
            {skylegsData?.total_sorties > 1 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    {skylegsData.total_sorties} Sorties:
                  </span>
                  <span className="text-xs text-slate-500">(click to select/deselect)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {skylegsData.sorties.map((sortie, idx) => (
                    <button
                      key={sortie.flight_id}
                      onClick={() => handleSortieChange(idx)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        selectedSortie.includes(idx)
                          ? 'bg-amber-600 text-white'
                          : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-100'
                      }`}
                      disabled={isLocked}
                    >
                      {sortie.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Divider between sortie and NOTAMs */}
            {skylegsData?.total_sorties > 1 && formData.ftxDesignator && (
              <div className="hidden sm:block h-6 w-px bg-slate-300" />
            )}
            
            {/* Include NOTAMs Checkbox - Show when FTX designator is selected */}
            {formData.ftxDesignator && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeNotamsTop"
                  checked={includeNotams}
                  onCheckedChange={(checked) => setIncludeNotams(checked)}
                  className="border-slate-400 data-[state=checked]:bg-indigo-600"
                  data-testid="include-notams-top-checkbox"
                  disabled={isLocked}
                />
                <Label htmlFor="includeNotamsTop" className="text-sm text-slate-600 cursor-pointer font-medium">
                  Load NOTAMs
                </Label>
                {includeNotams && notamsLoading && (
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                )}
                {includeNotams && !notamsLoading && Object.keys(notams).length > 0 && (
                  <span className="text-xs text-emerald-600 font-medium">
                    ({Object.keys(notams).length} airports)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Non-standard routing checkbox - show when FTX designator is selected */}
      {formData.ftxDesignator && (
        <div className="mb-4 flex items-center space-x-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <Checkbox
            id="notStandardRouting"
            checked={notStandardRouting}
            onCheckedChange={(checked) => setNotStandardRouting(checked)}
            className="border-slate-400 data-[state=checked]:bg-slate-600"
            data-testid="not-standard-routing-checkbox"
            disabled={isLocked}
          />
          <Label htmlFor="notStandardRouting" className="text-sm text-slate-700 cursor-pointer font-medium">
            Non-standard routing for this mission
          </Label>
        </div>
      )}

      {/* Flight Route Weather Section - Shows when FTX designator is selected and standard routing */}
      {formData.ftxDesignator && !notStandardRouting && (
        <FlightRouteWeather 
          key="flight-route-weather"
          ftxDesignator={formData.ftxDesignator}
          onMapCapture={setRouteMapImage}
          onPredictionVisibilityChange={setShowAIPrediction}
          onWeatherPredictionChange={setWeatherPredictionData}
          onWindyMapsChange={setWindyMapsData}
          onWindySnapshotChange={setWindySnapshots}
          aiWeatherBriefing={effectiveExplanation}
          savedWindyMaps={isLoadedFromLibrary ? windyMapsData : null}
          savedWindySnapshots={isLoadedFromLibrary ? windySnapshots : null}
          skylegsMissionTiming={formData.missionWindowTiming}
          missionDate={formData.missionDate}
        />
      )}

      {/* AI Weather Prediction Section - Only show when we have weather data */}
      {(effectiveMetar || icao || formData.arrivalAirport) && (
        <div className="mb-6 weather-card p-4">
          <AIWeatherPrediction 
            icao={icao || formData.arrivalAirport}
            metar={effectiveMetar}
            taf={effectiveTaf}
            ftxDesignator={formData.ftxDesignator}
            onRouteAirportsFound={(data) => {
              // Auto-suggest airports from route analysis
              if (data.route_airports?.length > 0 && !formData.departureAirport) {
                // Could auto-fill departure airport
              }
            }}
          />
        </div>
      )}

      {/* WX Analysis by Crew Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Cloud className="w-4 h-4 text-indigo-600" />
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
            WX Analysis by Crew
          </h4>
        </div>

        <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
          <h5 className="text-xs font-bold text-slate-600 uppercase mb-3">Weather Data</h5>
          
          {/* Auto-populated weather data */}
          {isMultiAirport && effectiveMultiAirportData?.length > 0 ? (
            /* Multi-airport weather display */
            <div className="space-y-3 mb-4">
              {effectiveMultiAirportData.filter(airport => !hiddenWeatherAirports.includes(airport.icao)).map((airport) => (
                <div key={airport.icao} className="p-3 bg-white rounded-lg border border-slate-100 relative">
                  {!isLocked && (
                    <button
                      onClick={() => handleHideWeatherAirport(airport.icao)}
                      className="absolute top-2 right-2 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                      title={`Remove ${airport.icao} from briefing`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <div className={`flex items-center justify-between mb-2 ${!isLocked ? 'pr-6' : ''}`}>
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-indigo-600" />
                      <span className="font-bold text-sm">{airport.icao}</span>
                      <span className="text-xs text-slate-500">{getAirportName(airport.icao)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {(airport.metar?.raw || airport.taf?.raw) && (
                        <button
                          onClick={() => handleCopyWeather(airport, airport.icao)}
                          className="p-1 rounded hover:bg-slate-100 transition-colors"
                          title="Copy METAR/TAF to clipboard"
                        >
                          {copiedAirport === airport.icao ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                          )}
                        </button>
                      )}
                      {airport.metar?.flight_category && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${
                          airport.metar.flight_category === 'VFR' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                          airport.metar.flight_category === 'MVFR' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                          airport.metar.flight_category === 'IFR' ? 'bg-red-100 text-red-700 border-red-200' :
                          airport.metar.flight_category === 'LIFR' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                          'bg-slate-100 text-slate-700 border-slate-200'
                        }`}>
                          {airport.metar.flight_category === 'VFR' && <Sun className="w-3 h-3" />}
                          {airport.metar.flight_category === 'MVFR' && <Cloud className="w-3 h-3" />}
                          {airport.metar.flight_category === 'IFR' && <CloudFog className="w-3 h-3" />}
                          {airport.metar.flight_category === 'LIFR' && <CloudOff className="w-3 h-3" />}
                          {airport.metar.flight_category}
                        </span>
                      )}
                    </div>
                  </div>
                  {airport.metar?.raw && (
                    <div className="mb-2">
                      <p className="text-xs text-slate-500 uppercase">METAR</p>
                      <p className="font-mono text-xs text-slate-700">{airport.metar.raw}</p>
                    </div>
                  )}
                  {airport.taf?.raw && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase">TAF</p>
                      <p className="font-mono text-xs text-slate-700">{airport.taf.raw}</p>
                    </div>
                  )}
                  {!airport.metar?.raw && !airport.taf?.raw && (
                    <p className="text-xs text-slate-400 italic">No weather data available</p>
                  )}
                </div>
              ))}
            </div>
          ) : (effectiveMetar || effectiveTaf) && !hiddenSingleAirport ? (
            <div className="space-y-3 mb-4">
              <div className="p-3 bg-white rounded-lg border border-slate-100 relative">
                {!isLocked && (
                  <button
                    onClick={handleHideSingleAirport}
                    className="absolute top-2 right-2 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    title={`Remove ${icao} from briefing`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <div className={`flex items-center justify-between mb-2 ${!isLocked ? 'pr-6' : ''}`}>
                  <div className="flex items-center gap-2">
                    <Plane className="w-4 h-4 text-indigo-600" />
                    <span className="font-bold text-sm">{icao}</span>
                    <span className="text-xs text-slate-500">{getAirportName(icao)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(effectiveMetar || effectiveTaf) && (
                      <button
                        onClick={() => handleCopyWeather({ metar: { raw: effectiveMetar }, taf: { raw: effectiveTaf } }, icao)}
                        className="p-1 rounded hover:bg-slate-100 transition-colors"
                        title="Copy METAR/TAF to clipboard"
                      >
                        {copiedAirport === icao ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                        )}
                      </button>
                    )}
                    {effectiveFlightCategory && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${
                        effectiveFlightCategory === 'VFR' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                        effectiveFlightCategory === 'MVFR' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                        effectiveFlightCategory === 'IFR' ? 'bg-red-100 text-red-700 border-red-200' :
                        effectiveFlightCategory === 'LIFR' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                        'bg-slate-100 text-slate-700 border-slate-200'
                      }`}>
                        {effectiveFlightCategory === 'VFR' && <Sun className="w-3 h-3" />}
                        {effectiveFlightCategory === 'MVFR' && <Cloud className="w-3 h-3" />}
                        {effectiveFlightCategory === 'IFR' && <CloudFog className="w-3 h-3" />}
                        {effectiveFlightCategory === 'LIFR' && <CloudOff className="w-3 h-3" />}
                        {effectiveFlightCategory}
                      </span>
                    )}
                  </div>
                </div>
                {effectiveMetar && (
                  <div className="mb-2">
                    <p className="text-xs text-slate-500 uppercase">METAR</p>
                    <p className="font-mono text-xs text-slate-700">{effectiveMetar}</p>
                  </div>
                )}
                {effectiveTaf && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase">TAF</p>
                    <p className="font-mono text-xs text-slate-700">{effectiveTaf}</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
          
          <Textarea
            placeholder="Enter additional mission weather notes..."
            value={formData.missionWeather}
            onChange={(e) => handleInputChange('missionWeather', e.target.value)}
            className="bg-white border-slate-300 min-h-[100px] resize-y"
            style={{ resize: 'vertical' }}
            data-testid="mission-weather-textarea"
            disabled={isLocked}
          />
        </div>

        {/* Weather Radar Image Section - DISABLED
        {icao && (
          <div className="mt-4 bg-slate-50 rounded-lg border border-slate-200 p-4">
            <h5 className="text-xs font-bold text-slate-600 uppercase mb-3 flex items-center gap-2">
              <CloudRain className="w-4 h-4 text-blue-600" />
              Weather Radar
            </h5>
            
            {radarLoading ? (
              <div className="flex items-center justify-center p-8 bg-white rounded-lg border border-slate-100">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                <span className="text-slate-600">Loading radar image...</span>
              </div>
            ) : radarImage ? (
              <div className="p-3 bg-white rounded-lg border border-slate-100">
                <div className="relative">
                  <img 
                    src={radarImage.url} 
                    alt={`Weather radar for ${icao}`}
                    className="w-full max-w-md mx-auto rounded-lg shadow-sm"
                    crossOrigin="anonymous"
                    data-testid="radar-image"
                  />
                  <div className="mt-2 text-center text-xs text-slate-500">
                    Radar data updated at {radarImage.timestamp} UTC
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center p-8 bg-white rounded-lg border border-slate-100">
                <span className="text-slate-500 text-sm">No radar data available for this location</span>
              </div>
            )}
          </div>
        )}
        */}
      </div>

      {/* Flight Limitations Section */}
      <div className="mb-6">
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
          <h5 className="text-xs font-bold text-slate-600 uppercase mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Flight Limitations
          </h5>
        
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <Checkbox
              id="poorVisibility"
              checked={formData.poorVisibility}
              onCheckedChange={() => handleCheckboxChange('poorVisibility')}
              className="border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              data-testid="poor-visibility-checkbox"
              disabled={isLocked}
            />
            <Label htmlFor="poorVisibility" className="text-sm text-slate-700 cursor-pointer">
              Poor visibility
            </Label>
          </div>
          
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <Checkbox
              id="lowClouds"
              checked={formData.lowClouds}
              onCheckedChange={() => handleCheckboxChange('lowClouds')}
              className="border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              data-testid="low-clouds-checkbox"
              disabled={isLocked}
            />
            <Label htmlFor="lowClouds" className="text-sm text-slate-700 cursor-pointer">
              Low clouds
            </Label>
          </div>
          
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <Checkbox
              id="crosswind"
              checked={formData.crosswind}
              onCheckedChange={() => handleCheckboxChange('crosswind')}
              className="border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              data-testid="crosswind-checkbox"
              disabled={isLocked}
            />
            <Label htmlFor="crosswind" className="text-sm text-slate-700 cursor-pointer">
              Crosswind &gt; 20 KTS
            </Label>
          </div>
          
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <Checkbox
              id="thunderstorm"
              checked={formData.thunderstorm}
              onCheckedChange={() => handleCheckboxChange('thunderstorm')}
              className="border-slate-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
              data-testid="thunderstorm-checkbox"
              disabled={isLocked}
            />
            <Label htmlFor="thunderstorm" className="text-sm text-slate-700 cursor-pointer">
              Thunderstorm / CB
            </Label>
          </div>
          
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <Checkbox
              id="icingConditions"
              checked={formData.icingConditions}
              onCheckedChange={() => handleCheckboxChange('icingConditions')}
              className="border-slate-400 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
              data-testid="icing-checkbox"
              disabled={isLocked}
            />
            <Label htmlFor="icingConditions" className="text-sm text-slate-700 cursor-pointer">
              Known icing conditions
            </Label>
          </div>
        </div>
        </div>
      </div>

      {/* Crew Recommendations Section */}
      <div className="mb-6">
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
          <h5 className="text-xs font-bold text-slate-600 uppercase mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-blue-600" />
            Crew Recommendations
          </h5>
        
          <Textarea
            placeholder="Enter crew recommendations, operational notes, fuel requirements, route adjustments..."
            value={formData.crewRecommendations}
            onChange={(e) => handleInputChange('crewRecommendations', e.target.value)}
            className="bg-white border-slate-300 min-h-[100px] resize-y"
            style={{ resize: 'vertical' }}
            data-testid="crew-recommendations-textarea"
            disabled={isLocked}
          />
        </div>

        {/* NOTAMs Section */}
        <div className="mt-4 flex items-center justify-between py-3">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="includeNotams"
              checked={includeNotams}
              onCheckedChange={handleIncludeNotamsChange}
              className="border-slate-400 data-[state=checked]:bg-slate-700"
              data-testid="include-notams-checkbox"
            />
            <Label htmlFor="includeNotams" className="text-sm text-slate-600 cursor-pointer font-medium">
              Include NOTAMs for route airports
            </Label>
          </div>
          <div className="flex items-center gap-4">
            {dismissedNotams.size > 0 && (
              <button
                onClick={restoreAllNotams}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Restore all ({dismissedNotams.size})
              </button>
            )}
            {includeNotams && Object.keys(notams).length > 0 && (
              <button
                onClick={() => setShowRawNotams(!showRawNotams)}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                {showRawNotams ? 'Plain Language' : 'Raw Format'}
              </button>
            )}
          </div>
        </div>

        {includeNotams && (
          <div className="mt-2">
            {notamsLoading ? (
              <div className="flex items-center justify-center p-6 bg-slate-50 rounded-lg border border-slate-200">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-600 mr-2" />
                <span className="text-slate-600 text-sm">Loading NOTAMs for route airports...</span>
              </div>
            ) : notamExplanationsLoading ? (
              <div className="flex items-center justify-center p-6 bg-slate-50 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-600 mr-2" />
                <span className="text-slate-600 text-sm">Translating NOTAMs to plain language...</span>
              </div>
            ) : Object.keys(getFilteredNotams()).length > 0 ? (
              <div className="space-y-6">
                {Object.entries(getFilteredNotams()).map(([airportIcao, airportNotams]) => {
                  const explanation = getFilteredExplanations()[airportIcao];
                  return (
                    <div key={airportIcao}>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                        <AlertTriangle className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-semibold text-slate-700 tracking-wide">
                          {airportIcao}
                        </span>
                        <span className="text-xs text-slate-400">
                          {airportNotams.length} {airportNotams.length === 1 ? 'notice' : 'notices'}
                        </span>
                        <button
                          onClick={() => dismissAirportNotams(airportIcao)}
                          className="ml-auto text-xs text-slate-400 hover:text-red-500 transition-colors"
                          title={`Dismiss all NOTAMs for ${airportIcao}`}
                          data-testid={`dismiss-airport-${airportIcao}`}
                        >
                          Dismiss all
                        </button>
                      </div>
                      
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {showRawNotams ? (
                          airportNotams.map((notam, index) => (
                            <div 
                              key={index} 
                              className="w-full py-3 px-4 bg-slate-50 rounded-lg group relative hover:bg-slate-100 transition-colors"
                            >
                              <button
                                onClick={() => dismissNotam(airportIcao, index, notam)}
                                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white rounded-full text-slate-400 hover:text-red-500"
                                title="Dismiss"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <div className="text-sm font-mono text-slate-600 leading-relaxed pr-8">
                                {notam}
                              </div>
                            </div>
                          ))
                        ) : explanation?.items?.length > 0 ? (
                          explanation.items.map((item, index) => {
                            const originalNotam = airportNotams.find(n => n.includes(item.id));
                            return (
                              <div 
                                key={index} 
                                className="w-full py-3 px-4 bg-slate-50 rounded-lg group relative hover:bg-slate-100 transition-colors"
                              >
                                <button
                                  onClick={() => dismissNotam(airportIcao, index, originalNotam || item.id)}
                                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white rounded-full text-slate-400 hover:text-red-500"
                                  title="Dismiss"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                                <div className="grid grid-cols-[200px_1fr] gap-6 pr-8 items-start">
                                  <div className="flex flex-col gap-1">
                                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                      {item.category}
                                    </span>
                                    <span className="text-xs font-mono text-slate-400">{item.id}</span>
                                  </div>
                                  <p className="text-sm text-slate-700 leading-relaxed">{item.plain}</p>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          airportNotams.map((notam, index) => (
                            <div 
                              key={index} 
                              className="w-full py-3 px-4 bg-slate-50 rounded-lg group relative hover:bg-slate-100 transition-colors"
                            >
                              <button
                                onClick={() => dismissNotam(airportIcao, index, notam)}
                                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white rounded-full text-slate-400 hover:text-red-500"
                                title="Dismiss"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <div className="text-sm font-mono text-slate-600 leading-relaxed pr-8">
                                {notam}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="text-xs text-slate-400 text-center pt-2">
                  {Object.values(getFilteredNotams()).flat().length} notices across {Object.keys(getFilteredNotams()).length} airports
                  {dismissedNotams.size > 0 && ` · ${dismissedNotams.size} dismissed`}
                </div>
              </div>
            ) : Object.keys(notams).length > 0 && dismissedNotams.size > 0 ? (
              <div className="p-4 rounded-lg bg-slate-50 text-center">
                <p className="text-sm text-slate-500 mb-2">All NOTAMs dismissed for this mission</p>
                <button
                  onClick={restoreAllNotams}
                  className="text-sm text-emerald-600 hover:text-emerald-800 underline"
                >
                  Restore all NOTAMs
                </button>
              </div>
            ) : (weatherPredictionData?.weatherData?.length > 0 || multiAirportData?.length > 0 || icao) ? (
              <div className="p-4 rounded-lg bg-slate-50 text-center">
                <p className="text-sm text-slate-500">No NOTAMs available for route airports</p>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-slate-50 text-center">
                <p className="text-sm text-slate-500">Select an FTX designator or enter airports to fetch NOTAMs</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Crew Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-cyan-600" />
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
            Crew
          </h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Pilot in Command (PIC) <span className="text-red-500">*</span>
            </Label>
            <Popover 
              open={picComboOpen && !isLocked} 
              onOpenChange={(open) => {
                setPicComboOpen(open);
                if (open) {
                  // Clear search when opening to show full list
                  setPicSearchValue("");
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={picComboOpen}
                  className="w-full justify-between bg-white border-slate-300 text-left font-normal hover:bg-slate-50"
                  data-testid="crew-pic-input"
                  disabled={isLocked}
                >
                  {formData.crewPIC || "Select or enter PIC..."}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search or enter name..." 
                    value={picSearchValue}
                    onValueChange={(value) => setPicSearchValue(value)}
                  />
                  <CommandList>
                    <CommandEmpty>
                      <div className="p-2 text-sm text-slate-500">
                        {picSearchValue ? (
                          <button 
                            className="w-full text-left hover:text-emerald-600"
                            onClick={() => {
                              handleInputChange('crewPIC', picSearchValue.toUpperCase());
                              setPicComboOpen(false);
                            }}
                          >
                            Use &quot;{picSearchValue.toUpperCase()}&quot;
                          </button>
                        ) : (
                          "Type to search or enter custom name"
                        )}
                      </div>
                    </CommandEmpty>
                    <CommandGroup heading="Crew Members">
                      {PIC_CREW_MEMBERS.map((member) => (
                        <CommandItem
                          key={member.value}
                          value={member.value}
                          onSelect={(currentValue) => {
                            handleInputChange('crewPIC', currentValue.toUpperCase());
                            setPicComboOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              formData.crewPIC === member.value ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          {member.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              PM
            </Label>
            <Input
              placeholder="Enter PM name"
              value={formData.crewPM}
              onChange={(e) => handleInputChange('crewPM', e.target.value)}
              className="bg-white border-slate-300"
              data-testid="crew-pm-input"
              disabled={isLocked}
            />
          </div>

          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Sensor Operator (SO)
            </Label>
            <Input
              placeholder="Enter Sensor Operator..."
              value={formData.crewSensorOperator || ""}
              onChange={(e) => handleInputChange('crewSensorOperator', e.target.value)}
              className="bg-white border-slate-300"
              data-testid="crew-so-input"
              disabled={isLocked}
            />
          </div>

          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Liaison Officer
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between bg-white border-slate-300"
                  disabled={isLocked}
                >
                  {formData.crewLiaisonOfficer || "Select or enter Liaison Officer..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" style={{ zIndex: 9999 }}>
                <Command>
                  <CommandInput 
                    placeholder="Search or type name..." 
                    value={formData.crewLiaisonOfficer}
                    onValueChange={(value) => handleInputChange('crewLiaisonOfficer', value)}
                  />
                  <CommandList>
                    <CommandEmpty>
                      <div className="p-2 text-sm text-slate-500">
                        Press Enter to use &quot;{formData.crewLiaisonOfficer}&quot;
                      </div>
                    </CommandEmpty>
                    <CommandGroup>
                      {LIAISON_OFFICERS.map((officer) => (
                        <CommandItem
                          key={officer.value}
                          value={officer.value}
                          onSelect={(currentValue) => {
                            handleInputChange('crewLiaisonOfficer', officer.label);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              formData.crewLiaisonOfficer === officer.label ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {officer.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* SkyLegs Loading Indicator */}
        {skylegsLoading && (
          <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading flight data from SkyLegs...</span>
          </div>
        )}

        {/* Mobile Download Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full mt-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 sm:hidden"
              disabled={generating}
              data-testid="download-btn-mobile"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                  <ChevronDown className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-56">
            <DropdownMenuItem onClick={handleDownloadPDF} className="cursor-pointer">
              <FileText className="w-4 h-4 mr-2" />
              Download as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDownloadHTML} className="cursor-pointer">
              <FileCode className="w-4 h-4 mr-2" />
              Download as HTML
            </DropdownMenuItem>
            {weatherPredictionData && (
              <DropdownMenuItem onClick={handleSendLO} className="cursor-pointer text-indigo-600">
                <Send className="w-4 h-4 mr-2" />
                Send LO
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Footer Note */}
      <div className="text-center text-xs text-slate-500 pt-4 border-t border-slate-200">
        All times in Zulu (UTC)
      </div>

      {/* Locked indicator when viewing old briefing */}
      {isLocked && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700">This briefing is locked (created more than 4 hours ago). Fields are read-only.</span>
        </div>
      )}

      {/* Skull emoji for superuser access - only visible on locked briefings */}
      {isLocked && !isSuperuser && (
        <button
          onClick={() => setShowSuperuserDialog(true)}
          className="fixed bottom-4 left-4 p-2 text-slate-300 hover:text-slate-500 transition-colors opacity-50 hover:opacity-100"
          title="Not your business"
        >
          <Skull className="w-5 h-5" />
        </button>
      )}

      {/* Superuser Password Dialog */}
      <Dialog open={showSuperuserDialog} onOpenChange={setShowSuperuserDialog}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Skull className="w-5 h-5" />
              Not your business
            </DialogTitle>
            <DialogDescription>
              There is no need to be here. Problems ahead.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="password"
              placeholder="Enter password"
              value={superuserPassword}
              onChange={(e) => setSuperuserPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSuperuserLogin()}
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuperuserDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSuperuserLogin}>
              Unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BriefingForm;
